<template>
  <div class="admin-prefeitura-dashboard">
    <div class="header">
      <h2>{{ greetingMessage }}</h2>
      <el-button @click="userStore.logout()" type="primary">Logout</el-button>
    </div>

    <el-tabs v-model="activeTab">
      <!-- Usuários Tab -->
      <el-tab-pane label="Usuários" name="users">
        <div class="tab-content flex-container">
          <!-- Registration Form -->
          <div class="flex-item">
            <h3>Criar Usuário</h3>
            <el-form :model="newUser" :rules="userRules" ref="userFormRef" label-position="top">
              <el-form-item label="Nome" prop="nome">
                <el-input v-model="newUser.nome" />
              </el-form-item>
              <el-form-item label="Email" prop="email">
                <el-input v-model="newUser.email" />
              </el-form-item>
              <el-form-item label="Senha" prop="password">
                <el-input v-model="newUser.password" type="password" />
              </el-form-item>
              <el-form-item label="CPF" prop="cpf">
                <el-input v-model="newUser.cpf" />
              </el-form-item>
              <el-form-item label="Celular" prop="celular">
                <el-input v-model="newUser.celular" />
              </el-form-item>
              <el-form-item label="Telefone" prop="telefone">
                <el-input v-model="newUser.telefone" />
              </el-form-item>
              <el-form-item label="Cargo" prop="role">
                <el-select v-model="newUser.role" placeholder="Selecione uma função">
                  <el-option label="Admin Prefeitura" value="admin_prefeitura"></el-option>
                  <el-option label="Cemiterio" value="cemiterio"></el-option>
                  <el-option label="Funeraria" value="funeraria"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="Cemitério ID (opcional)" prop="cemiterioId">
                <el-input v-model="newUser.cemiterioId" />
              </el-form-item>
              <el-form-item label="Funeraria ID (opcional)" prop="funerariaId">
                <el-input v-model="newUser.funerariaId" />
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="onSubmitUserForm">Criar Usuário</el-button>
              </el-form-item>
            </el-form>
          </div>
          <!-- Existing Users Table -->
          <div class="flex-item">
            <h3>Usuários Existentes</h3>
            <PaginatedTable
              api-endpoint="/users/paginated"
              :show-filters="true"
              ref="usersTableRef"
            >
              <template #filters="{ filters, updateFilter }">
                <el-col :span="8">
                  <el-form-item label="Nome">
                    <el-input
                      :model-value="filters.nome"
                      @input="(value) => updateFilter('nome', value)"
                      placeholder="Filtrar por nome..."
                      clearable
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="Email">
                    <el-input
                      :model-value="filters.email"
                      @input="(value) => updateFilter('email', value)"
                      placeholder="Filtrar por email..."
                      clearable
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="Função">
                    <el-select
                      :model-value="filters.role"
                      @change="(value) => updateFilter('role', value)"
                      placeholder="Selecionar função"
                      clearable
                    >
                      <el-option label="Admin Prefeitura" value="admin_prefeitura" />
                      <el-option label="Cemitério" value="cemiterio" />
                      <el-option label="Funerária" value="funeraria" />
                    </el-select>
                  </el-form-item>
                </el-col>
              </template>

              <el-table-column prop="id" label="ID" width="120" />
              <el-table-column prop="nome" label="Nome" />
              <el-table-column prop="email" label="Email" />
              <el-table-column label="Origem">
                <template #default="{ row }">
                  <span v-if="row.role === 'admin_prefeitura'">Prefeitura</span>
                  <span v-else-if="row.role === 'cemiterio'">Cemitério</span>
                  <span v-else-if="row.role === 'funeraria'">Funerária</span>
                  <span v-else>{{ row.role }}</span>
                </template>
              </el-table-column>
              <el-table-column label="Nome do Local">
                <template #default="{ row }">
                  <span v-if="row.role === 'admin_prefeitura'">Prefeitura</span>
                  <span v-else-if="row.role === 'cemiterio'">
                    {{ row.cemiterio ? row.cemiterio.nome : 'N/A' }}
                  </span>
                  <span v-else-if="row.role === 'funeraria'">
                    {{ row.funeraria ? row.funeraria.nome : 'N/A' }}
                  </span>
                  <span v-else>N/A</span>
                </template>
              </el-table-column>
              <el-table-column label="Ações" width="120">
                <template #default="{ row }">
                  <el-popconfirm title="Tem certeza que deseja excluir?" @confirm="deleteUser(row.id)">
                    <template #reference>
                      <el-button type="danger" size="small">Excluir</el-button>
                    </template>
                  </el-popconfirm>
                </template>
              </el-table-column>
            </PaginatedTable>
          </div>
        </div>
      </el-tab-pane>

      <!-- Cemitérios Tab -->
      <el-tab-pane label="Cemitérios e Funerárias" name="cemiterios">
        <div class="tab-content flex-container">
          <!-- Registration Form -->
          <div class="flex-item">
            <h3>Gerenciar Cemitérios</h3>
            <el-form :model="newCemiterio" ref="cemiterioFormRef" label-position="top">
              <el-form-item label="Nome" prop="nome">
                <el-input v-model="newCemiterio.nome" />
              </el-form-item>
              <el-form-item label="Endereço" prop="endereco">
                <el-input v-model="newCemiterio.endereco" />
              </el-form-item>
              <el-form-item label="Telefone" prop="telefone">
                <el-input v-model="newCemiterio.telefone" />
              </el-form-item>
              <el-form-item label="Email" prop="email">
                <el-input v-model="newCemiterio.email" />
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="createCemiterio">Criar</el-button>
              </el-form-item>
            </el-form>
          </div>
          <!-- Existing Cemitérios Table -->
          <div class="flex-item">
            <h3>Cemitérios Existentes</h3>
            <el-input v-model="cemiteriosFilter" placeholder="Filtrar cemitérios..." style="margin: 10px 0; max-width: 300px;" />
            <el-table :data="filteredCemiterios" v-loading="loadingCemiterios" class="mt-20" style="width: 100%">
              <el-table-column prop="id" label="Cemitério ID" />
              <el-table-column prop="nome" label="Nome" />
              <el-table-column prop="endereco" label="Endereço" />
              <el-table-column prop="telefone" label="Telefone" />
              <el-table-column prop="email" label="Email" />
              <el-table-column prop="ativo" label="Ativo" />
              <el-table-column label="Ações">
                <template #default="{ row }">
                  <el-button @click="openEditCemiterioDialog(row)" size="small">Editar</el-button>
                  <el-popconfirm title="Tem certeza que deseja excluir?" @confirm="deleteCemiterio(row.id)">
                    <template #reference>
                      <el-button type="danger" size="small">Excluir</el-button>
                    </template>
                  </el-popconfirm>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <!-- Edit Cemiterio Dialog -->
        <el-dialog v-model="editCemiterioDialogVisible" title="Editar Cemitério">
          <el-form :model="editCemiterio" label-position="top">
            <el-form-item label="Nome" prop="nome">
              <el-input v-model="editCemiterio.nome" />
            </el-form-item>
            <el-form-item label="Endereço" prop="endereco">
              <el-input v-model="editCemiterio.endereco" />
            </el-form-item>
            <el-form-item label="Telefone" prop="telefone">
              <el-input v-model="editCemiterio.telefone" />
            </el-form-item>
            <el-form-item label="Email" prop="email">
              <el-input v-model="editCemiterio.email" />
            </el-form-item>
            <el-form-item label="Ativo" prop="ativo">
              <el-switch v-model="editCemiterio.ativo" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="editCemiterioDialogVisible = false">Cancelar</el-button>
            <el-button type="primary" @click="updateCemiterio">Salvar</el-button>
          </template>
        </el-dialog>

        <!-- Funerarias Section (moved here) -->
        <div class="tab-content flex-container" style="margin-top: 40px;">
          <!-- Registration Form -->
          <div class="flex-item">
            <h3>Gerenciar Funerarias</h3>
            <el-form :model="newFuneraria" ref="funerariaFormRef" label-position="top">
              <el-form-item label="Nome" prop="nome">
                <el-input v-model="newFuneraria.nome" />
              </el-form-item>
              <el-form-item label="CNPJ" prop="cnpj">
                <el-input v-model="newFuneraria.cnpj" />
              </el-form-item>
              <el-form-item label="Endereço" prop="endereco">
                <el-input v-model="newFuneraria.endereco" />
              </el-form-item>
              <el-form-item label="Telefone" prop="telefone">
                <el-input v-model="newFuneraria.telefone" />
              </el-form-item>
              <el-form-item label="Email" prop="email">
                <el-input v-model="newFuneraria.email" />
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="createFuneraria">Criar</el-button>
              </el-form-item>
            </el-form>
          </div>
          <!-- Existing Funerarias Table -->
          <div class="flex-item">
            <h3>Funerarias Existentes</h3>
            <el-input v-model="funerariasFilter" placeholder="Filtrar funerarias..." style="margin: 10px 0; max-width: 300px;" />
            <el-table :data="filteredFunerarias" v-loading="loadingFunerarias" class="mt-20" style="width: 100%">
              <el-table-column prop="id" label="Funeraria ID" />
              <el-table-column prop="nome" label="Nome" />
              <el-table-column prop="cnpj" label="CNPJ" />
              <el-table-column prop="endereco" label="Endereço" />
              <el-table-column prop="telefone" label="Telefone" />
              <el-table-column prop="email" label="Email" />
              <el-table-column prop="ativo" label="Ativo" />
              <el-table-column label="Ações">
                <template #default="{ row }">
                  <el-button @click="openEditFunerariaDialog(row)" size="small">Editar</el-button>
                  <el-popconfirm title="Tem certeza que deseja bloquear/desbloquear essa funerária?" @confirm="toggleFunerariaStatus(row)">
                    <template #reference>
                      <el-button type="warning" size="small">
                        {{ row.ativo ? 'Bloquear' : 'Desbloquear' }}
                      </el-button>
                    </template>
                  </el-popconfirm>
                  <el-popconfirm title="Tem certeza que deseja excluir?" @confirm="deleteFuneraria(row.id)">
                    <template #reference>
                      <el-button type="danger" size="small">Excluir</el-button>
                    </template>
                  </el-popconfirm>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <!-- Edit Funeraria Dialog -->
        <el-dialog v-model="editFunerariaDialogVisible" title="Editar Funeraria">
          <el-form :model="editFuneraria" label-position="top">
            <el-form-item label="Nome" prop="nome">
              <el-input v-model="editFuneraria.nome" />
            </el-form-item>
            <el-form-item label="CNPJ" prop="cnpj">
              <el-input v-model="editFuneraria.cnpj" />
            </el-form-item>
            <el-form-item label="Endereço" prop="endereco">
              <el-input v-model="editFuneraria.endereco" />
            </el-form-item>
            <el-form-item label="Telefone" prop="telefone">
              <el-input v-model="editFuneraria.telefone" />
            </el-form-item>
            <el-form-item label="Email" prop="email">
              <el-input v-model="editFuneraria.email" />
            </el-form-item>
            <el-form-item label="Ativo" prop="ativo">
              <el-switch v-model="editFuneraria.ativo" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="editFunerariaDialogVisible = false">Cancelar</el-button>
            <el-button type="primary" @click="updateFuneraria">Salvar</el-button>
          </template>
        </el-dialog>
      </el-tab-pane>

      <!-- Vagas Tab -->
      <el-tab-pane label="Vagas" name="vagas">
        <div class="tab-content">
          <h3>Lista de Vagas</h3>
          <PaginatedTable
            api-endpoint="/vagas/admin/paginated"
            :show-filters="true"
            ref="vagasTableRef"
          >
            <template #filters="{ filters, updateFilter }">
              <el-col :span="5">
                <el-form-item label="Quadra">
                  <el-input
                    :model-value="filters.quadra"
                    @input="(value) => updateFilter('quadra', value)"
                    placeholder="Filtrar quadra..."
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="5">
                <el-form-item label="Nº Sepultura">
                  <el-input
                    :model-value="filters.numeroSepultura"
                    @input="(value) => updateFilter('numeroSepultura', value)"
                    placeholder="Filtrar sepultura..."
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="5">
                <el-form-item label="Tipo">
                  <el-select
                    :model-value="filters.tipo"
                    @change="(value) => updateFilter('tipo', value)"
                    placeholder="Selecionar tipo"
                    clearable
                  >
                    <el-option label="Carneira" value="Carneira" />
                    <el-option label="Carneira Anjo" value="Carneira Anjo" />
                    <el-option label="Gaveta" value="Gaveta" />
                    <el-option label="Gaveta Anjo" value="Gaveta Anjo" />
                    <el-option label="Perpétua - Gaveta" value="Perpétua - Gaveta" />
                    <el-option label="Perpétua - Carneira" value="Perpétua - Carneira" />
                    <el-option label="Banqueta" value="Banqueta" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="5">
                <el-form-item label="Cemitério">
                  <el-input
                    :model-value="filters.cemiterio"
                    @input="(value) => updateFilter('cemiterio', value)"
                    placeholder="Filtrar cemitério..."
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="4">
                <el-form-item label="Disponível">
                  <el-select
                    :model-value="filters.disponivel"
                    @change="(value) => updateFilter('disponivel', value)"
                    placeholder="Selecionar"
                    clearable
                  >
                    <el-option label="Sim" :value="true" />
                    <el-option label="Não" :value="false" />
                  </el-select>
                </el-form-item>
              </el-col>
            </template>

            <el-table-column prop="id" label="ID" min-width="150" show-overflow-tooltip />
            <el-table-column prop="quadra" label="Quadra" width="80" />
            <el-table-column prop="numeroSepultura" label="Nº Sepultura" width="120" />
            <el-table-column label="Tipo">
              <template #default="{ row }">
                <span>
                  {{ row.tipo || 'N/A' }}
                  <template v-if="row.obeso">
                    (A:{{ row.altura }} L:{{ row.largura }})
                  </template>
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Cemitério">
              <template #default="{ row }">
                {{ row.cemiterio ? row.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column prop="disponivel" label="Disponível" :formatter="formatDisponivel" width="100" />
            <el-table-column label="Ações" width="180">
              <template #default="{ row }">
                <el-button v-if="canEditPrivileged" size="small" @click="editVaga(row)">Editar</el-button>
                <el-popconfirm title="Tem certeza que deseja excluir?" @confirm="deleteVaga(row.id)">
                  <template #reference>
                    <el-button type="danger" size="small">Excluir</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </PaginatedTable>
        </div>

        <!-- Edit Vaga Dialog -->
        <el-dialog v-model="editVagaDialogVisible" title="Editar Vaga">
          <el-form :model="editVagaData" label-position="top">
            <el-form-item label="Quadra">
              <el-input v-model="editVagaData.quadra" />
            </el-form-item>
            <el-form-item label="Nº Sepultura">
              <el-input v-model="editVagaData.numeroSepultura" />
            </el-form-item>
            <el-form-item label="Tipo">
              <el-input v-model="editVagaData.tipo" />
            </el-form-item>
            <el-form-item label="Disponível">
              <el-switch v-model="editVagaData.disponivel" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="editVagaDialogVisible = false">Cancelar</el-button>
            <el-button type="primary" @click="updateVaga">Salvar</el-button>
          </template>
        </el-dialog>
      </el-tab-pane>

      <el-tab-pane name="requisicoes">
        <template #label>
          <el-badge
            :value="pendenciasCount.pendentes"
            :hidden="!pendenciasCount.pendentes"
            type="danger"
          >
            Requisições
          </el-badge>
        </template>
        <div class="tab-content">
          <h3>Lista de Requisições</h3>
          <div style="margin: 8px 0;">
            <el-tag type="warning" effect="plain" style="margin-right: 8px;">
              {{ pendenciasCount.pendentes }} pendente(s) de permissão
            </el-tag>
            <el-tag type="info" effect="plain">
              {{ pendenciasCount.semComprovante }} sem comprovante
            </el-tag>
          </div>
          <PaginatedTable
            api-endpoint="/requisicoes/paginated"
            :show-filters="true"
            :show-date-filters="true"
            :date-filter-fields="['dataInicio', 'dataFim']"
            ref="requisicoesTableRef"
          >
            <template #filters="{ filters, updateFilter }">
              <el-col :span="6">
                <el-form-item label="Nome do Falecido">
                  <el-input
                    :model-value="filters.nomeFalecido"
                    @input="(value) => updateFilter('nomeFalecido', value)"
                    placeholder="Filtrar por nome..."
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="Documento (sem caracteres)">
                  <el-input
                    :model-value="filters.cpfFalecido"
                    @input="(value) => updateFilter('cpfFalecido', value)"
                    placeholder="Filtrar por Documento..."
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="Status">
                  <el-select
                    :model-value="filters.status"
                    @change="(value) => updateFilter('status', value)"
                    placeholder="Selecionar status"
                    clearable
                  >
                    <el-option label="Pendente" value="PENDENTE" />
                    <el-option label="Permitido" value="PERMITIDO" />
                    <el-option label="Confirmado" value="CONFIRMADO" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="Cemitério">
                  <el-input
                    :model-value="filters.cemiterio"
                    @input="(value) => updateFilter('cemiterio', value)"
                    placeholder="Filtrar por cemitério..."
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="Funerária">
                  <el-input
                    :model-value="filters.funeraria"
                    @input="(value) => updateFilter('funeraria', value)"
                    placeholder="Filtrar por funerária..."
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="6">
                <el-form-item label="ID da DO">
                  <el-input
                    :model-value="filters.doId"
                    @input="(value) => updateFilter('doId', value)"
                    placeholder="Filtrar por ID da DO..."
                    clearable
                  />
                </el-form-item>
              </el-col>
            </template>

            <!-- ID da Requisição -->
            <el-table-column prop="id" label="ID" min-width="150" show-overflow-tooltip />
            <!-- Nome do Falecido -->
            <el-table-column prop="nomeFalecido" label="Nome do Falecido" />
            <!-- Falecido Obeso? -->
            <el-table-column label="Obeso?" width="90">
              <template #default="{ row }">
                {{ row.obeso ? 'Sim' : 'Não' }}
              </template>
            </el-table-column>
            <!-- CPF -->
            <el-table-column prop="tipoDocumento" label="Tipo de Documento" />
            <el-table-column prop="cpfFalecido" label="Documento" />
            <!-- ID da DO -->
            <el-table-column prop="doId" label="ID da DO" />
            <!-- Data de Falecimento -->
            <el-table-column prop="dataFalecimento" label="Data de Falecimento" :formatter="formatDateCell" />
            <!-- Horário Desejado -->
            <el-table-column label="Horário Desejado">
              <template #default="{ row }">
                {{ formatHorarioDesejado(row) }}
              </template>
            </el-table-column>
            <!-- Cemitério -->
            <el-table-column label="Cemitério">
              <template #default="{ row }">
                {{ row.vaga && row.vaga.cemiterio ? row.vaga.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <!-- Funerária -->
            <el-table-column label="Funerária">
              <template #default="{ row }">
                {{ row.funeraria ? row.funeraria.nome : 'N/A' }}
              </template>
            </el-table-column>
            <!-- Vaga -->
            <el-table-column label="Vaga">
              <template #default="{ row }">
                <span v-if="row.vaga">
                  Quadra: {{ row.vaga.quadra }} /
                  Nº Sepultura: {{ row.vaga.numeroSepultura }} /
                  Tipo: {{ row.vaga.tipo }}
                  <template v-if="row.vaga.obeso">
                    (Obeso — A:{{ row.vaga.altura !== null ? row.vaga.altura : 'N/A' }} L:{{ row.vaga.largura !== null ? row.vaga.largura : 'N/A' }})
                  </template>
                </span>
                <span v-else>N/A</span>
              </template>
            </el-table-column>
            <!-- Status -->
            <el-table-column prop="status" label="Status" />
            <!-- Criado Em -->
            <el-table-column prop="criadoEm" label="Criado Em" :formatter="formatDateCell" />
            <!-- Atualizado Em -->
            <el-table-column prop="atualizadoEm" label="Atualizado Em" :formatter="formatDateCell" />
            <!-- RG e DO Column -->
            <el-table-column label="RG e DO">
              <template #default="{ row }">
                <div v-if="row.caminhoArquivos">
                  <template v-for="(file, index) in parseCaminhoArquivos(row.caminhoArquivos)" :key="index">
                    <template v-if="index < 2">
                      <a href="#" @click.prevent="handleDownloadFile(row.id, index)">
                        {{ index === 0 ? 'RG' : 'DO' }}
                      </a>
                      <span v-if="index === 0 && parseCaminhoArquivos(row.caminhoArquivos).length > 1"> | </span>
                    </template>
                  </template>
                </div>
                <div v-else>N/A</div>
              </template>
            </el-table-column>
            <!-- Comprovante de Pagamento Column -->
            <el-table-column v-if="false" label="Comprovante de Pagamento">
              <template #default="{ row }">
                <a
                  v-if="parseCaminhoArquivos(row.caminhoArquivos).length > 2"
                  href="#"
                  @click.prevent="handleDownloadFile(row.id, 2)"
                >
                  Ver comprovante do pagamento
                </a>
                <span v-else style="color: #b0b0b0;">Não enviado</span>
              </template>
            </el-table-column>
            <!-- Arquivos Extras Column -->
            <el-table-column label="Arquivos Extras">
              <template #default="{ row }">
                <div v-if="parseArquivosExtras(row.arquivosExtras).length > 0">
                  <div v-for="(file, index) in parseArquivosExtras(row.arquivosExtras)" :key="index">
                    <a href="#" @click.prevent="handleDownloadExtraFile(row.id, index)">
                      {{ file.originalName }}
                    </a>
                  </div>
                </div>
                <span v-else style="color: #b0b0b0;">Nenhum</span>
              </template>
            </el-table-column>
            <!-- Ações -->
            <el-table-column label="Ações" width="280">
              <template #default="{ row }">
                <template v-if="row.status !== 'CONFIRMADO'">
                  <el-button size="small" type="info" @click="toggleRequisicaoStatus(row)">
                    {{ row.status === 'PERMITIDO' ? 'Retirar' : 'Permitir' }}
                  </el-button>
                  <el-button v-if="canEditPrivileged" size="small" @click="editRequisicao(row)">Editar</el-button>
                  <el-button type="danger" size="small" @click="openDeleteRequisicaoDialog(row)">Excluir</el-button>
                </template>
              </template>
            </el-table-column>
          </PaginatedTable>
        </div>
      </el-tab-pane>

      <el-tab-pane label="Mapa de Sepultamento" name="mapa">
        <div class="mapa-container">
          <div class="filtro">
            <!-- Cemiterio Filter -->
            <el-select
                v-model="selectedCemiterio"
                placeholder="Filtrar por Cemitério (Todos)"
                clearable
            >
              <el-option
                  v-for="cem in cemiteriosList"
                  :key="cem.id"
                  :label="cem.nome"
                  :value="cem.id"
              />
            </el-select>

            <!-- Baixar Mapa Radio Group -->
            <div style="margin-top: 10px;">
              <span>Baixar Mapa:</span>
              <el-radio-group v-model="mapDownloadMode" style="margin-left: 10px;">
                <el-radio label="dia_exato">Dia Exato</el-radio>
                <el-radio label="intervalo">Intervalo</el-radio>
              </el-radio-group>
            </div>

            <!-- Date Pickers based on selected mode -->
            <div style="margin-top: 10px;">
              <el-date-picker
                  v-if="mapDownloadMode === 'dia_exato'"
                  v-model="selectedDate"
                  type="date"
                  placeholder="Selecione a data"
              />
              <div v-else>
                <el-date-picker
                    v-model="startDate"
                    type="date"
                    placeholder="Data de Início"
                    style="margin-right: 10px;"
                    :disabledDate="disabledStartDate"
                />
                <el-date-picker
                    v-model="endDate"
                    type="date"
                    placeholder="Data Final"
                    :disabledDate="disabledEndDate"
                />
              </div>
            </div>

            <el-button type="primary" @click="downloadPDFMap" style="margin-left: 10px;">
              Baixar PDF
            </el-button>
          </div>

          <!-- New table: Approved Requisições -->
          <h3 class="mt-20">Requisições Aprovadas</h3>
          <el-input v-model="approvedRequisicoesFilter" placeholder="Filtrar requisições..." style="margin: 10px 0; max-width: 300px;" />
          <el-table :data="filteredApprovedRequisicoes" class="mt-20" style="width: 100%">
            <el-table-column label="Quadra">
              <template #default="{ row }">
                {{ row.vaga?.quadra || 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Nº Sepultura">
              <template #default="{ row }">
                {{ row.vaga?.numeroSepultura || 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Tipo">
              <template #default="{ row }">
                <span>
                  {{ row.vaga.tipo || 'N/A' }}
                  <template v-if="row.vaga.obeso">
                    (A:{{ row.vaga.altura !== null ? row.vaga.altura : 'N/A' }} L:{{ row.vaga.largura !== null ? row.vaga.largura : 'N/A' }})
                  </template>
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Cemitério">
              <template #default="{ row }">
                {{ row.vaga?.cemiterio ? row.vaga.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Funerária">
              <template #default="{ row }">
                {{ row.funeraria ? row.funeraria.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column prop="nomeFalecido" label="Nome do Falecido" />
            <el-table-column label="Obeso?" width="90">
              <template #default="{ row }">{{ row.obeso ? 'Sim' : 'Não' }}</template>
            </el-table-column>
            <el-table-column label="Documento">
              <template #default="{ row }">
                ({{ row.tipoDocumento }}:) {{ row.cpfFalecido }}
              </template>
            </el-table-column>
            <el-table-column label="Data e Hora">
              <template #default="{ row }">
                {{ formatDateCell(null, null, row.dataRequisicao) }} {{ row.horaRequisicao }}
              </template>
            </el-table-column>
            <!-- New columns added to the mapa tab -->
            <el-table-column label="Confirmação">
              <template #default="{ row }">
                {{ row.confirmDateTime ? formatDateToRio(row.confirmDateTime) : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Horário Real de Sepultamento">
              <template #default="{ row }">
                {{ row.horarioSepultamento ? formatDateToRio(row.horarioSepultamento) : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Ações">
              <template #default="{ row }">
                <el-button
                    type="primary"
                    size="small"
                    @click="confirmSepultamento(row)"
                    v-if="row.status !== 'CONFIRMADO'">
                  Confirmar sepultamento
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>
      <el-tab-pane label="Gráficos (Em Construção)" name="graficos">
        <!-- Date filters -->
        <div class="charts-filter" style="margin-bottom: 16px; display: flex; gap: 8px; align-items: center;">
          <el-date-picker v-model="chartStartDate" type="date" placeholder="Data Início" />
          <el-date-picker v-model="chartEndDate" type="date" placeholder="Data Fim" />
          <el-button type="primary" @click="fetchCharts">Atualizar</el-button>
        </div>

        <div class="charts-container" style="display: flex; flex-direction: column; gap: 40px;">
          <div style="width: 100%; height: 400px;">
          <v-chart ref="requestsPerDayChart" :option="requestsPerDayOptions" style="width: 100%; height: 400px;" />
          </div>
          <div style="width: 100%; height: 400px;">
          <v-chart ref="statusDistributionChart" :option="statusDistributionOptions" style="width: 100%; height: 400px;" />
          </div>
          <div style="width: 100%; height: 400px;">
          <v-chart ref="requestsPerFunerariaChart" :option="requestsPerFunerariaOptions" style="width: 100%; height: 400px;" />
          </div>
          <div style="width: 100%; height: 400px;">
          <v-chart ref="requestsPerCemiterioChart" :option="requestsPerCemiterioOptions" style="width: 100%; height: 400px;" />
          </div>
          <div style="width: 100%; height: 400px;">
          <v-chart ref="vacanciesByTypeChart" :option="vacanciesByTypeOptions" style="width: 100%; height: 400px;" />
          </div>
          <div style="width: 100%; height: 400px;">
          <v-chart ref="vacancyOccupationChart" :option="vacancyOccupationOptions" style="width: 100%; height: 400px;" />
          </div>
          <div style="width: 100%; height: 400px;">
          <v-chart ref="requestsMatrixChart" :option="requestsMatrixOptions" style="width: 100%; height: 400px;" />
          </div>
          <div style="width: 100%; height: 400px;">
          <v-chart ref="availableVagasChart" :option="availableVagasOptions" style="width: 100%; height: 400px;" />
          </div>
        </div>
      </el-tab-pane>

      <!-- Status de Funerárias Tab -->
      <el-tab-pane label="Status de Funerárias (Em Testes)" name="bloqueios">
        <div class="tab-content">
          <h3>Monitoramento de Status das Funerárias</h3>
          
          <!-- Filtros -->
          <el-row gutter="16" style="margin-bottom: 20px;">
            <el-col :span="8">
              <el-input
                v-model="funerariasStatusFilter"
                placeholder="Filtrar por nome da funerária..."
                clearable
              />
            </el-col>
            <el-col :span="8">
              <el-button @click="fetchFunerariasStatus" type="primary" :loading="loadingFunerariasStatus">
                Atualizar Lista
              </el-button>
            </el-col>
          </el-row>

          <!-- Tabela de Status das Funerárias -->
          <el-table :data="filteredFunerariasStatus" v-loading="loadingFunerariasStatus" style="width: 100%">
            <el-table-column prop="funerariaName" label="Funerária" width="200" />
            
            <el-table-column label="Status" width="120">
              <template #default="{ row }">
                <el-tag :type="isBlocked(row) ? 'danger' : 'success'">
                  {{ isBlocked(row) ? 'Bloqueada' : 'Ativa' }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column label="Reservas Ativas" width="120" align="center">
              <template #default="{ row }">
                <el-badge :value="row.activeReservationsCount" :type="row.activeReservationsCount > 0 ? 'primary' : 'info'">
                  <span>{{ row.activeReservationsCount }}</span>
                </el-badge>
              </template>
            </el-table-column>

            <el-table-column label="Tempo até Desbloqueio" width="180">
              <template #default="{ row }">
                <span v-if="isBlocked(row)" class="text-warning">
                  {{ formatMinutesRemaining(row.minutesUntilUnblocked) }}
                </span>
                <span v-else class="text-success">N/A</span>
              </template>
            </el-table-column>

            <el-table-column label="Detalhes das Reservas" min-width="300">
              <template #default="{ row }">
                <div v-if="row.reservations && row.reservations.length > 0">
                  <el-popover
                    placement="top"
                    :width="400"
                    trigger="hover"
                  >
                    <template #reference>
                      <el-button size="small" type="info" plain>
                        Ver {{ row.reservations.length }} reserva(s)
                      </el-button>
                    </template>
                    <div>
                      <div v-for="reservation in row.reservations" :key="reservation.vagaId" style="margin-bottom: 10px;">
                        <strong>Vaga {{ reservation.vagaNumero }}</strong><br>
                        <small>{{ reservation.cemiterioName }}</small><br>
                        <small class="text-warning">Expira em: {{ formatMinutesRemaining(reservation.minutesRemaining) }}</small>
                      </div>
                    </div>
                  </el-popover>
                </div>
                <span v-else class="text-muted">Nenhuma reserva</span>
              </template>
            </el-table-column>

            <el-table-column label="Ações" width="150">
              <template #default="{ row }">
                <el-button
                  v-if="isBlocked(row)"
                  size="small"
                  type="danger"
                  @click="confirmRemoveBloqueio(row)"
                  :loading="removingBloqueio === row.funerariaId"
                  :disabled="!row.funerariaId"
                >
                  Desbloquear
                </el-button>
                <span v-else class="text-success">Ativa</span>
              </template>
            </el-table-column>
          </el-table>

          <!-- Empty state -->
          <div v-if="!loadingFunerariasStatus && funerariasStatus.length === 0" class="empty-state">
            <p>Nenhuma funerária encontrada.</p>
          </div>

          <!-- Legenda -->
          <div style="margin-top: 20px; padding: 15px; background-color: #f5f7fa; border-radius: 4px;">
            <h4 style="margin-top: 0;">Legenda:</h4>
            <ul style="margin: 0; padding-left: 20px;">
              <li><strong>Ativa:</strong> Funerária pode fazer reservas normalmente</li>
              <li><strong>Bloqueada:</strong> Funerária temporariamente impedida de fazer novas reservas</li>
              <li><strong>Reservas Ativas:</strong> Número de vagas atualmente reservadas pela funerária</li>
              <li><strong>Tempo até Desbloqueio:</strong> Tempo restante até o bloqueio expirar automaticamente</li>
            </ul>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="Registro de Livro" name="registroLivro">
        <div class="tab-content">
          <div style="display: flex; align-items: center; gap: 12px;">
            <h3 style="margin: 0;">Registro de Livro</h3>
            <el-button size="small" type="primary" plain @click="refreshRegistrosLivro">Atualizar</el-button>
            <span style="font-size: 12px; color: #909399;">Clique numa linha para ver a entrada completa.</span>
          </div>
          <PaginatedTable
            ref="registroLivroTableRef"
            api-endpoint="/registros-livro/paginated"
            :show-filters="true"
            style="margin-top: 12px;"
            @row-click="openRegistroDetail"
          >
            <template #filters="{ filters, updateFilter }">
              <el-col :span="6">
                <el-form-item label="Cemitério">
                  <el-select
                    :model-value="filters.cemiterioId"
                    @change="(v) => updateFilter('cemiterioId', v)"
                    placeholder="Todos os cemitérios"
                    clearable
                    filterable
                  >
                    <el-option v-for="c in cemiteriosList" :key="c.id" :label="c.nome" :value="c.id" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="6" v-for="f in livroFilterFields" :key="f.key">
                <el-form-item :label="f.label">
                  <el-date-picker
                    v-if="f.type === 'date'"
                    :model-value="filters[f.key]"
                    @update:model-value="(v) => updateFilter(f.key, v)"
                    type="date"
                    value-format="YYYY-MM-DD"
                    style="width: 100%;"
                    clearable
                  />
                  <el-input
                    v-else
                    :model-value="filters[f.key]"
                    @input="(v) => updateFilter(f.key, v)"
                    clearable
                  />
                </el-form-item>
              </el-col>
            </template>
            <el-table-column label="Cemitério" min-width="140" show-overflow-tooltip>
              <template #default="{ row }">{{ row.cemiterio ? row.cemiterio.nome : 'N/A' }}</template>
            </el-table-column>
            <el-table-column prop="numeroOrdem" label="Nº Ordem" min-width="90" />
            <el-table-column prop="nomeFalecido" label="Falecido" min-width="160" show-overflow-tooltip />
            <el-table-column prop="data" label="Data" min-width="110" />
            <el-table-column prop="tipoSepultura" label="Tipo Sep." min-width="110" />
            <el-table-column prop="quadra" label="Quadra" min-width="80" />
            <el-table-column prop="numeroSepultura" label="Nº Sep." min-width="90" />
            <el-table-column prop="nomeResponsavel" label="Responsável" min-width="150" show-overflow-tooltip />
            <el-table-column label="Confirmação" min-width="170">
              <template #default="{ row }">
                <div v-if="row.confirmedAt" style="line-height: 1.2;">
                  <span>{{ row.confirmedBy?.nome || row.confirmedBy?.id || 'Confirmado' }}</span><br>
                  <small style="color: #909399;">{{ formatDateToRio(row.confirmedAt) }}</small>
                </div>
                <div v-else @click.stop>
                  <el-popconfirm 
                    title="Tem certeza que deseja confirmar os dados?" 
                    confirm-button-text="Sim"
                    cancel-button-text="Não"
                    width="220px"
                    @confirm="confirmarRegistroLivro(row)"
                  >
                    <template #reference>
                      <el-button type="success" size="small">Confirmar Dados</el-button>
                    </template>
                  </el-popconfirm>
                </div>
              </template>
            </el-table-column>
          </PaginatedTable>
        </div>
      </el-tab-pane>

      <el-tab-pane v-if="canViewLogs" label="Logs" name="logs">
        <div class="tab-content">
          <h3>Logs do Sistema (somente leitura)</h3>
          <el-alert
            type="info"
            :closable="false"
            show-icon
            title="Comece pela tabela Auditoria (audit_logs)"
            description="A audit_logs é a tabela mais concisa e deve ser consultada primeiro: ela guarda os dados verdadeiros de antes e depois de cada alteração (criação, edição e exclusão). As demais fontes são complementares."
            style="margin-bottom: 12px;"
          />
          <el-alert
            type="warning"
            :closable="false"
            show-icon
            title="Fuso horário dos registros"
            description="O servidor de banco de dados opera em UTC, portanto os horários são gravados em UTC. O horário de Brasília (America/Sao_Paulo) está 3 horas atrás do UTC. Considere essa diferença de 3 horas ao interpretar as datas e horas exibidas abaixo."
            style="margin-bottom: 12px;"
          />
          <div style="margin: 12px 0; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
            <span>Fonte:</span>
            <el-select v-model="logSource" style="width: 260px;" @change="onLogSourceChange">
              <el-option v-for="s in logSources" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </div>
          <div style="margin: 8px 0 12px; display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;">
            <div v-for="f in currentLogFilters" :key="f.key" style="display: flex; flex-direction: column; gap: 4px;">
              <span style="font-size: 12px; color: #606266;">{{ f.label }}</span>
              <el-select
                v-if="f.type === 'userSelect'"
                v-model="logFilters.userId"
                filterable
                clearable
                allow-create
                default-first-option
                placeholder="Nome ou ID"
                style="width: 220px;"
              >
                <el-option v-for="u in logUsers" :key="u.id" :label="`${u.nome} (${u.role})`" :value="u.id" />
              </el-select>
              <el-select
                v-else-if="f.type === 'select'"
                v-model="logFilters[f.key]"
                clearable
                placeholder="Todos"
                style="width: 180px;"
              >
                <el-option v-for="o in f.options" :key="o" :label="o" :value="o" />
              </el-select>
              <el-date-picker
                v-else-if="f.type === 'daterange'"
                v-model="logFilters.date"
                type="daterange"
                value-format="YYYY-MM-DD"
                range-separator="até"
                start-placeholder="Início"
                end-placeholder="Fim"
                style="width: 260px;"
              />
              <el-input v-else v-model="logFilters[f.key]" clearable placeholder="Filtrar..." style="width: 200px;" />
            </div>
            <el-button size="small" type="primary" @click="applyLogFilters">Filtrar</el-button>
            <el-button size="small" @click="clearLogFilters">Limpar</el-button>
          </div>
          <el-table
            :data="logsData"
            v-loading="logsLoading"
            border
            stripe
            size="small"
            highlight-current-row
            style="width: 100%; font-family: 'Courier New', Courier, monospace; cursor: pointer;"
            table-layout="auto"
            @row-click="openLogDetail"
          >
            <el-table-column
              v-for="def in currentLogColumns"
              :key="def.key"
              :label="def.label"
              min-width="150"
              show-overflow-tooltip
            >
              <template #default="{ row }">
                {{ logCellValue(row, def) }}
              </template>
            </el-table-column>
          </el-table>
          <div style="margin-top: 12px; display: flex; justify-content: center;">
            <el-pagination
              layout="total, prev, pager, next"
              :total="logsTotal"
              :page-size="logsLimit"
              :current-page="logsPage"
              @current-change="onLogsPageChange"
            />
          </div>

          <el-dialog v-model="logDetailVisible" title="Detalhes do registro de log" width="760px">
            <div v-if="logDetailRow">
              <div v-for="def in currentLogColumns" :key="def.key" style="margin-bottom: 10px;">
                <div style="font-size: 12px; color: #909399;">{{ def.label }}</div>
                <pre
                  v-if="def.type === 'json'"
                  style="white-space: pre-wrap; word-break: break-word; background: #f5f7fa; padding: 8px; border-radius: 4px; margin: 2px 0; font-size: 12px;"
                >{{ logDetailJson(logDetailRow[def.key]) }}</pre>
                <div v-else style="font-weight: 500;">{{ logCellValue(logDetailRow, def) || '—' }}</div>
              </div>
            </div>
            <template #footer>
              <el-button @click="logDetailVisible = false">Fechar</el-button>
            </template>
          </el-dialog>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>

  <!-- Diálogo de Edição de Requisição -->
  <el-dialog v-model="registroDetailVisible" title="Registro de Livro — entrada completa" width="760px">
    <div v-if="registroDetailRow">
      <el-row :gutter="16">
        <el-col :span="12" v-for="f in livroDetailFields" :key="f.key" style="margin-bottom: 8px;">
          <div style="font-size: 12px; color: #909399;">{{ f.label }}</div>
          <div style="font-weight: 500; white-space: pre-wrap;">{{ registroCellValue(registroDetailRow, f) || '—' }}</div>
        </el-col>
      </el-row>
    </div>
    <template #footer>
      <el-button @click="registroDetailVisible = false">Fechar</el-button>
    </template>
  </el-dialog>

  <el-dialog v-model="editRequisicaoDialogVisible" title="Editar Requisição">
    <el-form :model="editRequisicaoData" ref="editRequisicaoFormRef" label-position="top">
      <el-form-item label="Nome do Falecido" prop="nomeFalecido">
        <el-input v-model="editRequisicaoData.nomeFalecido" />
        <div style="font-size: 12px; color: #909399;">
          Edição de requisição restrita a usuários autorizados.
        </div>
      </el-form-item>

      <el-form-item label="Data da Requisição" prop="dataRequisicao">
        <el-date-picker
            v-model="editRequisicaoData.dataRequisicao"
            type="date"
            placeholder="Selecione a data"
            :disabled-date="disabledRequisicaoDateEdit"
            @change="onEditDateChange"
        />
      </el-form-item>

      <el-form-item label="Hora da Requisição" prop="horaRequisicao">
        <el-select
            v-model="editRequisicaoData.horaRequisicao"
            placeholder="Selecione o horário"
            :disabled="!editRequisicaoData.dataRequisicao"
            v-loading="loadingTimeSlotsEdit"
        >
          <el-option
              v-for="item in availableTimeSlotsEdit"
              :key="item.value"
              :label="item.value"
              :value="item.value"
              :disabled="item.disabled"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Motivo da Edição" prop="reason" :rules="[{ required: true, message: 'O motivo é obrigatório', trigger: 'blur' }]">
        <el-input
          v-model="editRequisicaoData.reason"
          type="textarea"
          placeholder="Ex: Mudança de horário autorizada por X"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="editRequisicaoDialogVisible = false">Cancelar</el-button>
      <el-button type="primary" @click="updateRequisicao">Salvar</el-button>
    </template>
  </el-dialog>

  <!-- Diálogo de Exclusão de Requisição -->
  <el-dialog v-model="deleteRequisicaoDialogVisible" title="Excluir Requisição" width="500px">
    <div style="margin-bottom: 20px;">
      <p><strong>Atenção:</strong> Esta ação irá excluir a requisição e o motivo será enviado para a funerária.</p>
    </div>
    <el-form :model="deleteRequisicaoData" ref="deleteRequisicaoFormRef" label-position="top">
      <el-form-item label="Motivo da Exclusão" prop="reason" required>
        <el-input
          v-model="deleteRequisicaoData.reason"
          type="textarea"
          :rows="4"
          placeholder="Digite o motivo da exclusão da requisição..."
          maxlength="500"
          show-word-limit
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="deleteRequisicaoDialogVisible = false">Cancelar</el-button>
      <el-button type="danger" @click="confirmDeleteRequisicao" :loading="deletingRequisicao">Excluir</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from "vue";
import { useUserStore } from "@/stores/user";
import apiClient from "@/plugins/axios";
import { ElMessage, ElMessageBox } from "element-plus";
import PaginatedTable from "@/components/PaginatedTable.vue";

import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import {
  LineChart, BarChart, PieChart
} from 'echarts/charts'
import {
  TitleComponent, TooltipComponent, LegendComponent, GridComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([
  LineChart,
  BarChart,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  CanvasRenderer
])

// --- date filters for charts (default last 30 days) ---
const today = new Date().toISOString().substr(0,10)
const prior = new Date()
prior.setDate(prior.getDate() - 30)
const chartStartDate = ref(prior.toISOString().substr(0,10))
const chartEndDate   = ref(today)

let intervalId;

// --- chart options ---

const requestsPerDayChart = ref(null);
const statusDistributionChart = ref(null);
const requestsPerFunerariaChart = ref(null);
const requestsPerCemiterioChart = ref(null);
const vacanciesByTypeChart = ref(null);
const vacancyOccupationChart = ref(null);
const requestsMatrixChart = ref(null);
const availableVagasChart = ref(null);

async function resizeAllCharts() {
  await nextTick(); // Wait for DOM update first!
  const chartRefs = [
    requestsPerDayChart,
    statusDistributionChart,
    requestsPerFunerariaChart,
    requestsPerCemiterioChart,
    vacanciesByTypeChart,
    vacancyOccupationChart,
    requestsMatrixChart,
    availableVagasChart,
  ];

  chartRefs.forEach(chartRef => {
    if (chartRef.value && chartRef.value.resize) {
      chartRef.value.resize();
    }
  });
}

let observer = null;

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        resizeAllCharts();
      }
    });
  }, { root: null });

  const chartContainer = document.querySelector('.charts-container');
  if (chartContainer) observer.observe(chartContainer);
});

onUnmounted(() => {
  if (observer) observer.disconnect();
  if (intervalId) clearInterval(intervalId);
});

const requestsPerDayOptions = ref({})

const statusDistributionOptions = ref({})

// 3. Requisições por Funerária
const requestsPerFunerariaOptions = ref({})

// 4. Requisições por Cemitério
const requestsPerCemiterioOptions = ref({})

// 5. Vagas por Tipo e Cemitério
const vacanciesByTypeOptions = ref({})

// 6. % Ocupação de Vagas
const vacancyOccupationOptions = ref({})

// 7. Requisições Funerária × Cemitério (stacked)
const requestsMatrixOptions = ref({})

// 8. Vagas Disponíveis
const availableVagasOptions = ref({})


async function fetchCharts() {
  const params = { start: chartStartDate.value, end: chartEndDate.value };

  // 1. Requisições por Dia
  {
    const res = await apiClient.get('/dashboard/requests-per-day', { params });
    const data = res.data;
    requestsPerDayOptions.value = {
      title: { text: 'Requisições por Dia', left: 'center' },
      tooltip: { trigger: 'axis' },
      legend: { bottom: 10, left: 'center', data: ['Requisições'] },
      grid: { containLabel: true, top: 60, left: 60, right: 30, bottom: 80 },
      xAxis: { type: 'category', data: data.map(d => d.date), axisLabel: { rotate: 45 } },
      yAxis: { type: 'value' },
      series: [{ name: 'Requisições', type: 'line', data: data.map(d => d.total) }]
    };
  }

  // 2. Status das Requisições
  {
    const res = await apiClient.get('/dashboard/status-distribution', { params });
    const data = res.data;
    statusDistributionOptions.value = {
      title: { text: 'Status das Requisições', left: 'center' },
      tooltip: { trigger: 'item' },
      legend: { orient: 'horizontal', bottom: 10, left: 'center' },
      series: [{
        type: 'pie',
        radius: ['40%', '60%'],
        data: data.map(d => ({ name: d.status, value: d.total }))
      }]
    };
  }

  // 3. Requisições por Funerária
  {
    const res = await apiClient.get('/dashboard/requests-per-funeraria', { params });
    const data = res.data;
    requestsPerFunerariaOptions.value = {
      title: { text: 'Requisições por Funerária', left: 'center' },
      tooltip: {},
      legend: { bottom: 10, left: 'center', data: ['Requisições'] },
      grid: { containLabel: true, top: 60, left: 60, right: 30, bottom: 80 },
      xAxis: { type: 'category', data: data.map(d => d.funeraria), axisLabel: { rotate: 30 } },
      yAxis: { type: 'value' },
      series: [{ name: 'Requisições', type: 'bar', data: data.map(d => d.total) }]
    };
  }

  // 4. Requisições por Cemitério
  {
    const res = await apiClient.get('/dashboard/requests-per-cemiterio', { params });
    const data = res.data;
    requestsPerCemiterioOptions.value = {
      title: { text: 'Requisições por Cemitério', left: 'center' },
      tooltip: {},
      legend: { bottom: 10, left: 'center', data: ['Requisições'] },
      grid: { containLabel: true, top: 60, left: 60, right: 30, bottom: 80 },
      xAxis: { type: 'category', data: data.map(d => d.cemiterio), axisLabel: { rotate: 30 } },
      yAxis: { type: 'value' },
      series: [{ name: 'Requisições', type: 'bar', data: data.map(d => d.total) }]
    };
  }

  // 5. Vagas por Tipo e Cemitério
  {
    const res = await apiClient.get('/dashboard/vacancies-by-type', { params });
    const data = res.data;
    const cemNames = [...new Set(data.map(d => d.cemiterio))];
    const types = [...new Set(data.map(d => d.tipo))];

    vacanciesByTypeOptions.value = {
      title: { text: 'Vagas por Tipo e Cemitério', left: 'center' },
      tooltip: {},
      legend: { bottom: 10, left: 'center', data: types },
      grid: { containLabel: true, top: 60, left: 60, right: 30, bottom: 80 },
      xAxis: { type: 'category', data: cemNames, axisLabel: { rotate: 30 } },
      yAxis: { type: 'value' },
      series: types.map(tipo => ({
        name: tipo,
        type: 'bar',
        data: cemNames.map(cem => {
          const rec = data.find(r => r.cemiterio === cem && r.tipo === tipo);
          return rec ? rec.total : 0;
        })
      }))
    };
  }

  // 6. % Ocupação de Vagas
  {
    const res = await apiClient.get('/dashboard/vacancy-occupation', { params });
    const data = res.data;

    vacancyOccupationOptions.value = {
      title: { text: '% Ocupação de Vagas', left: 'center' },
      tooltip: { trigger: 'axis' },
      legend: { bottom: 10, left: 'center', data: ['% Ocupação'] },
      grid: { containLabel: true, top: 60, left: 60, right: 30, bottom: 80 },
      xAxis: { type: 'category', data: data.map(d => d.cemiterio), axisLabel: { rotate: 30 } },
      yAxis: { type: 'value', axisLabel: { formatter: '{value} %' } },
      series: [{
        name: '% Ocupação',
        type: 'bar',
        data: data.map(d => d.occupationRate)
      }]
    };
  }

// 7. Requisições por Funerária e Cemitério (Stacked Bar Chart)
  {
    const res = await apiClient.get('/dashboard/requests-funeraria-cemiterio', { params });
    const data = res.data;

    const funerarias = [...new Set(data.map(d => d.funeraria))];
    const cemiterios = [...new Set(data.map(d => d.cemiterio))];

    requestsMatrixOptions.value = {
      title: { text: 'Requisições por Funerária e Cemitério', left: 'center' },
      tooltip: { trigger: 'axis' },
      legend: { bottom: 10, left: 'center', data: cemiterios },
      grid: { containLabel: true, top: 60, left: 60, right: 30, bottom: 100 },
      xAxis: { type: 'category', data: funerarias, axisLabel: { rotate: 30 } },
      yAxis: { type: 'value' },
      series: cemiterios.map(cem => ({
        name: cem,
        type: 'bar',
        stack: 'total',
        data: funerarias.map(fun => {
          const record = data.find(d => d.funeraria === fun && d.cemiterio === cem);
          return record ? record.total : 0;
        })
      }))
    };
  }

// 8. Vagas Disponíveis
  {
    const res = await apiClient.get('/dashboard/available-vagas');
    const data = res.data;

    availableVagasOptions.value = {
      title: { text: 'Vagas Disponíveis', left: 'center' },
      tooltip: {},
      legend: { bottom: 10, left: 'center', data: ['Vagas Disponíveis'] },
      grid: { containLabel: true, top: 60, left: 60, right: 30, bottom: 80 },
      xAxis: { type: 'category', data: data.map(d => d.cemiterio), axisLabel: { rotate: 30 } },
      yAxis: { type: 'value' },
      series: [{
        name: 'Vagas Disponíveis',
        type: 'bar',
        data: data.map(d => d.total)
      }]
    };
  }

  // Add other charts similarly following this clear pattern if needed.
}

function formatDateToRio(dateString) {
  if (!dateString) return 'N/A';
  
  try {
    // Se é apenas data (YYYY-MM-DD), adiciona horário zerado
    if (dateString.length === 10) {
      const dateObj = new Date(dateString + "T00:00:00");
      return dateObj.toLocaleDateString('pt-BR', { 
        timeZone: 'America/Sao_Paulo', 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } 
    
    // Para datetime completo
    const dateObj = new Date(dateString);
    
    // Verifica se a data é válida
    if (isNaN(dateObj.getTime())) {
      return 'Data inválida';
    }
    
    // Formatar com timezone do Brasil
    const timeStr = dateObj.toLocaleTimeString('pt-BR', { 
      timeZone: 'America/Sao_Paulo', 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
    
    const dateStr = dateObj.toLocaleDateString('pt-BR', { 
      timeZone: 'America/Sao_Paulo', 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
    
    return `${timeStr} ${dateStr}`;
  } catch (error) {
    console.error('Erro ao formatar data:', error, 'Input:', dateString);
    return 'Erro na formatação';
  }
}

// Função alternativa para forçar ajuste de timezone se necessário
function formatDateToBrazil(dateString) {
  if (!dateString) return 'N/A';
  
  try {
    const dateObj = new Date(dateString);
    
    if (isNaN(dateObj.getTime())) {
      return 'Data inválida';
    }
    
    // Ajustar para UTC-3 (horário do Brasil) manualmente se necessário
    // O JavaScript já deveria fazer isso com America/Sao_Paulo, mas como fallback:
    const brasilOffset = -3 * 60; // -3 horas em minutos
    const utcTime = dateObj.getTime() + (dateObj.getTimezoneOffset() * 60000);
    const brasilTime = new Date(utcTime + (brasilOffset * 60000));
    
    return brasilTime.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Erro ao formatar data para Brasil:', error);
    return 'Erro na formatação';
  }
}

function formatDateCell(row, column, cellValue) {
  return cellValue ? formatDateToRio(cellValue) : 'N/A';
}

const getErrorMessage = (error) => {
  if (error.response && error.response.data && error.response.data.message) {
    if (Array.isArray(error.response.data.message)) {
      return error.response.data.message.join(', ');
    }
    return error.response.data.message;
  }
  return 'Ocorreu um erro desconhecido. Tente novamente.';
};

export default {
  name: "AdminPrefeituraDashboard",
  components: {
    VChart,
    PaginatedTable,
    // ...any other components
  },
  setup() {
    const activeTab = ref("users");
    // when the "Gráficos" tab opens, fetch data and resize all charts
    watch(activeTab, async (tab) => {
      if (tab === 'graficos') {
        await fetchCharts();
        await resizeAllCharts();
      } else if (tab === 'users') {
        // PaginatedTable gerencia os dados automaticamente
      } else if (tab === 'cemiterios') {
        fetchCemiterios();
      } else if (tab === 'funerarias') {
        fetchFunerarias();
      } else if (tab === 'vagas') {
        // PaginatedTable gerencia os dados automaticamente
      } else if (tab === 'requisicoes') {
        // PaginatedTable gerencia os dados automaticamente
      } else if (tab === 'mapa') {
        if (new Date().getHours() >= 9) {
          fetchMapaSepultamento();
          fetchApprovedRequisicoes();
        }
      } else if (tab === 'bloqueios') {
        fetchFunerariasStatus();
      }
    });
    const userStore = useUserStore();
    const selectedCemiterio = ref(null);
    const cemiteriosList = ref([]);
    const mapaSepultamento = ref([]);

    // Variáveis para o diálogo de edição
    const editRequisicaoFormRef = ref(null);

    // Variáveis para o diálogo de exclusão
    const deleteRequisicaoDialogVisible = ref(false);
    const deleteRequisicaoFormRef = ref(null);
    const deleteRequisicaoData = ref({
      id: null,
      reason: ''
    });
    const deletingRequisicao = ref(false);

// Variáveis para upload de arquivos na edição
    const editRgFile = ref(null);
    const editDoFile = ref(null);
    const editRgFileName = ref('Nenhum arquivo selecionado');
    const editDoFileName = ref('Nenhum arquivo selecionado');

    const updateEditFileNames = () => {
      editRgFileName.value = editRgFile.value ? editRgFile.value.name : 'Nenhum arquivo selecionado';
      editDoFileName.value = editDoFile.value ? editDoFile.value.name : 'Nenhum arquivo selecionado';
    };

    const onEditRGFileChange = (file, fileListVal) => {
      if (fileListVal && fileListVal.length > 0) {
        editRgFile.value = fileListVal[0];
        updateEditFileNames();
      }
    };

    const onEditDOFileChange = (file, fileListVal) => {
      if (fileListVal && fileListVal.length > 0) {
        editDoFile.value = fileListVal[0];
        updateEditFileNames();
      }
    };

    const onEditRGFileRemove = () => {
      editRgFile.value = null;
      updateEditFileNames();
    };

    const onEditDOFileRemove = () => {
      editDoFile.value = null;
      updateEditFileNames();
    };

// Variáveis para buscar horários disponíveis na edição
    const availableTimeSlotsEdit = ref([]);
    const loadingTimeSlotsEdit = ref(false);

    // O endpoint /requisicoes/available-times espera a data em YYYY-MM-DD.
    // formatDateToRio devolve DD/MM/YYYY (formato de exibição) e por isso a
    // busca falhava. Aceita tanto Date (do date-picker) quanto string já em
    // YYYY-MM-DD (vinda da API ao abrir a edição).
    const toApiDate = (val) => {
      if (typeof val === 'string' && val.length === 10 && val[4] === '-') {
        return val;
      }
      const d = new Date(val);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const onEditDateChange = async (value) => {
      availableTimeSlotsEdit.value = [];
      if (value && editRequisicaoData.value.vagaId) {
        const formattedDate = toApiDate(value);
        loadingTimeSlotsEdit.value = true;
        try {
          const { data } = await apiClient.get('/requisicoes/available-times', {
            params: {
              vacancyId: editRequisicaoData.value.vagaId,
              date: formattedDate,
            },
          });
          availableTimeSlotsEdit.value = data;
        } catch (error) {
          console.error('Erro ao buscar horários disponíveis', error);
        } finally {
          loadingTimeSlotsEdit.value = false;
        }
      }
    };

    const disabledRequisicaoDateEdit = (date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date.getTime() < today.getTime();
    };

    // New reactive state for the Mapa download mode and date selection
    const mapDownloadMode = ref("dia_exato"); // "dia_exato" or "intervalo"
    const selectedDate = ref(null);
    const startDate = ref(null);
    const endDate = ref(null);

    // New reactive variable for approved requisitions
    const approvedRequisicoes = ref([]);

    // --- BLOQUEIOS DE FUNERÁRIAS ---
    const funerariasStatus = ref([]);
    const loadingFunerariasStatus = ref(false);
    const funerariasStatusFilter = ref('');
    const removingBloqueio = ref(null);

    const filteredFunerariasStatus = computed(() => {
      if (!funerariasStatusFilter.value) return funerariasStatus.value;
      return funerariasStatus.value.filter(funeraria => 
        funeraria.funerariaName?.toLowerCase().includes(funerariasStatusFilter.value.toLowerCase())
      );
    });

    const fetchFunerariasStatus = async () => {
      loadingFunerariasStatus.value = true;
      try {
        const { data } = await apiClient.get('/vagas/admin/funerarias/status');
        funerariasStatus.value = data;
      } catch (error) {
        console.error('Erro ao buscar status das funerárias:', error);
        ElMessage.error(getErrorMessage(error));
      } finally {
        loadingFunerariasStatus.value = false;
      }
    };

    const isBlocked = (funeraria) => {
      if (!funeraria) {
        console.warn('isBlocked: funeraria é null ou undefined');
        return false;
      }
      
      const blocked = funeraria.isBlacklisted && 
                     funeraria.blacklistExpiresAt && 
                     new Date(funeraria.blacklistExpiresAt) > new Date();
      
      // Debug log para ajudar na depuração
      console.debug('isBlocked check:', {
        funerariaName: funeraria.funerariaName,
        isBlacklisted: funeraria.isBlacklisted,
        blacklistExpiresAt: funeraria.blacklistExpiresAt,
        blocked: blocked
      });
      
      return blocked;
    };

    const formatDateTime = (dateTime) => {
      if (!dateTime) return 'N/A';
      return new Date(dateTime).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatMinutesRemaining = (minutes) => {
      if (!minutes || minutes <= 0) return 'Expirado';
      if (minutes < 60) return `${minutes} min`;
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}min`;
    };

    const confirmRemoveBloqueio = (funeraria) => {
      ElMessageBox.confirm(
        `Tem certeza que deseja remover o bloqueio da funerária "${funeraria.funerariaName}"?\n\nEsta ação será registrada no sistema de auditoria.`,
        'Confirmar remoção de bloqueio',
        {
          confirmButtonText: 'Sim, remover',
          cancelButtonText: 'Cancelar',
          type: 'warning',
          showInput: true,
          inputPlaceholder: 'Motivo para remoção (opcional)',
          inputType: 'textarea'
        }
      ).then((result) => {
        removeBloqueio(funeraria.funerariaId, result.value || 'Remoção manual pelo administrador');
      }).catch(() => {
        // Cancelado
      });
    };

    const removeBloqueio = async (funerariaId, reason) => {
      removingBloqueio.value = funerariaId;
      try {
        await apiClient.delete(`/vagas/admin/funerarias/${funerariaId}/blacklist`, {
          data: {
            funerariaId,
            reason
          }
        });
        ElMessage.success('Bloqueio removido com sucesso!');
        fetchFunerariasStatus(); // Recarregar a lista
      } catch (error) {
        console.error('Erro ao remover bloqueio:', error);
        ElMessage.error(getErrorMessage(error));
      } finally {
        removingBloqueio.value = null;
      }
    };

    // Functions to enforce a maximum interval of one year between startDate and endDate
    const disabledStartDate = (date) => {
      if (endDate.value) {
        const oneYearBefore = new Date(endDate.value);
        oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);
        return date.getTime() < oneYearBefore.getTime() || date.getTime() > new Date(endDate.value).getTime();
      }
      return false;
    };

    const disabledEndDate = (date) => {
      if (startDate.value) {
        const oneYearAfter = new Date(startDate.value);
        oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);
        return date.getTime() < new Date(startDate.value).getTime() || date.getTime() > oneYearAfter.getTime();
      }
      return false;
    };

    const parseCaminhoArquivos = (caminhoArquivos) => {
      try {
        return JSON.parse(caminhoArquivos);
      } catch (error) {
        return [];
      }
    };

    const parseArquivosExtras = (arquivosExtras) => {
      try {
        if (!arquivosExtras) return [];
        return JSON.parse(arquivosExtras);
      } catch (error) {
        return [];
      }
    };

    const cancelRequisicao = async (id) => {
      try {
        await apiClient.patch(`/requisicoes/${id}/cancelar`, null, {
          headers: { Authorization: `Bearer ${userStore.token}` }
        });
        ElMessage.success("Requisição cancelada com sucesso!");
        if (requisicoesTableRef.value) {
          requisicoesTableRef.value.refresh();
        }
        await fetchAdminVagas();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    // Updated fetchMapaSepultamento: build parameters conditionally
    const fetchMapaSepultamento = async () => {
      try {
        let params = {};
        let endpoint = "";
        if (mapDownloadMode.value === "dia_exato") {
          if (!selectedDate.value) return;
          params.data = selectedDate.value;
          endpoint = "/requisicoes/mapa";
        } else {
          if (!startDate.value || !endDate.value) return;
          params.dataInicio = startDate.value;
          params.dataFinal = endDate.value;
          endpoint = "/requisicoes/mapa/intervalo";
        }
        // Only add cemiterio filter if a value is selected
        if (selectedCemiterio.value) {
          params.cemiterio = selectedCemiterio.value;
        }
        const response = await apiClient.get(endpoint, { params });
        mapaSepultamento.value = response.data;
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    // Updated fetchApprovedRequisicoes: similar logic to fetchMapaSepultamento
    const fetchApprovedRequisicoes = async () => {
      try {
        let params = {};
        let endpoint = "";
        if (mapDownloadMode.value === "dia_exato") {
          if (!selectedDate.value) return;
          params.data = selectedDate.value;
          endpoint = "/requisicoes/mapa";
        } else {
          if (!startDate.value || !endDate.value) return;
          params.dataInicio = startDate.value;
          params.dataFinal = endDate.value;
          endpoint = "/requisicoes/mapa/intervalo";
        }
        if (selectedCemiterio.value) {
          params.cemiterio = selectedCemiterio.value;
        }
        const response = await apiClient.get(endpoint, { params });
        approvedRequisicoes.value = response.data;
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    const confirmSepultamento = async (row) => {
      try {
        const confirmDateTime = new Date().toISOString();
        await apiClient.patch(`/requisicoes/${row.id}/confirm`, { confirmDateTime });
        ElMessage.success("Sepultamento confirmado!");
        fetchApprovedRequisicoes();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    // Download PDF map (choose endpoint based on mode)
    const downloadPDFMap = async () => {
      try {
        let params = {};
        let endpoint = "";
        if (mapDownloadMode.value === "dia_exato") {
          if (!selectedDate.value) return;
          params.data = selectedDate.value;
          endpoint = "/requisicoes/mapa/pdf";
        } else {
          if (!startDate.value || !endDate.value) return;
          params.dataInicio = startDate.value;
          params.dataFinal = endDate.value;
          endpoint = "/requisicoes/mapa/intervalo/pdf";
        }
        if (selectedCemiterio.value) {
          params.cemiterio = selectedCemiterio.value;
        }
        const response = await apiClient.get(endpoint, { params, responseType: "blob" });
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", mapDownloadMode.value === "dia_exato" ? "mapa_sepulamento.pdf" : "mapa_sepulamento_intervalo.pdf");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    // ---------------- USERS ----------------
    const newUser = ref({
      nome: "",
      email: "",
      password: "",
      cpf: "",
      celular: "",
      telefone: "",
      role: "",
      cemiterioId: "",
      funerariaId: "",
    });
    const userFormRef = ref(null);
    const usersTableRef = ref(null);
    const vagasTableRef = ref(null);
    const requisicoesTableRef = ref(null);

    // #3 Prefeitura: contagem de pendências para as badges da dashboard.
    const pendenciasCount = ref({ pendentes: 0, semComprovante: 0 });
    const fetchPendenciasCount = async () => {
      try {
        const { data } = await apiClient.get("/requisicoes/pendencias-count");
        pendenciasCount.value = {
          pendentes: data.pendentes || 0,
          semComprovante: data.semComprovante || 0,
        };
      } catch (error) {
        console.error("Erro ao buscar contagem de pendências", error);
      }
    };

    // #1 Prefeitura: aba de logs (read-only, adaptativa, acesso por allowlist).
    const canViewLogs = ref(false);
    const logSources = ref([]);
    const logSource = ref("audit_logs");
    const logsData = ref([]);
    const logsTotal = ref(0);
    const logsPage = ref(1);
    const logsLimit = ref(25);
    const logsLoading = ref(false);
    const logUsers = ref([]);
    const logFilters = ref({});

    // Filtros que mudam reativamente conforme a fonte (tabela) selecionada.
    const logFilterDefs = {
      audit_logs: [
        { key: "userId", label: "Usuário", type: "userSelect" },
        { key: "action", label: "Ação", type: "select", options: ["CREATE", "UPDATE", "DELETE"] },
        { key: "entity", label: "Entidade", type: "text" },
        { key: "date", label: "Período", type: "daterange" },
      ],
      requisicoes_logs: [
        { key: "userId", label: "Usuário", type: "userSelect" },
        { key: "action", label: "Ação", type: "select", options: ["CREATE", "UPDATE", "DELETE", "CANCEL", "APPROVE", "REJECT"] },
        { key: "date", label: "Período", type: "daterange" },
      ],
      vagas_logs: [
        { key: "userId", label: "Usuário", type: "userSelect" },
        { key: "action", label: "Ação", type: "select", options: ["CREATE", "UPDATE", "DELETE"] },
        { key: "date", label: "Período", type: "daterange" },
      ],
      users_logs: [
        { key: "userId", label: "Usuário", type: "userSelect" },
        { key: "action", label: "Ação", type: "select", options: ["CREATE", "UPDATE", "DELETE"] },
        { key: "date", label: "Período", type: "daterange" },
      ],
      user_sessions: [
        { key: "userId", label: "Usuário", type: "userSelect" },
        { key: "status", label: "Status", type: "select", options: ["SUCCESS", "FAILED", "EXPIRED", "LOGGED_OUT", "SUSPENDED"] },
        { key: "date", label: "Período", type: "daterange" },
      ],
      file_access_logs: [
        { key: "userId", label: "Usuário", type: "userSelect" },
        { key: "date", label: "Período", type: "daterange" },
      ],
    };

    const currentLogFilters = computed(
      () => logFilterDefs[logSource.value] || [],
    );

    const logColumnDefs = {
      audit_logs: [
        { key: "created_at", label: "Data", type: "date" },
        { key: "user_id", label: "Usuário (ID)", type: "text" },
        { key: "entity", label: "Entidade", type: "text" },
        { key: "entity_id", label: "Registro (ID)", type: "text" },
        { key: "action", label: "Ação", type: "text" },
        { key: "endpoint", label: "Endpoint", type: "text" },
        { key: "before", label: "Antes", type: "json" },
        { key: "after", label: "Depois", type: "json" },
      ],
      requisicoes_logs: [
        { key: "createdAt", label: "Data", type: "date" },
        { key: "user", label: "Usuário", type: "user" },
        { key: "action", label: "Ação", type: "text" },
        { key: "nomeFalecido", label: "Falecido", type: "text" },
        { key: "requisicaoId", label: "Requisição (ID)", type: "text" },
        { key: "changes", label: "Alterações", type: "json" },
        { key: "details", label: "Detalhes", type: "json" },
      ],
      vagas_logs: [
        { key: "createdAt", label: "Data", type: "date" },
        { key: "user", label: "Usuário", type: "user" },
        { key: "action", label: "Ação", type: "text" },
        { key: "quadra", label: "Quadra", type: "text" },
        { key: "numeroSepultura", label: "Nº Sepultura", type: "text" },
        { key: "tipo", label: "Tipo", type: "text" },
        { key: "changes", label: "Alterações", type: "json" },
      ],
      users_logs: [
        { key: "createdAt", label: "Data", type: "date" },
        { key: "user", label: "Usuário", type: "user" },
        { key: "action", label: "Ação", type: "text" },
        { key: "targetUserId", label: "Usuário-alvo (ID)", type: "text" },
        { key: "changes", label: "Alterações", type: "json" },
        { key: "details", label: "Detalhes", type: "json" },
      ],
      user_sessions: [
        { key: "createdAt", label: "Data", type: "date" },
        { key: "user", label: "Usuário", type: "user" },
        { key: "ipAddress", label: "IP", type: "text" },
        { key: "geoIpCity", label: "Cidade", type: "text" },
        { key: "internetProvider", label: "Provedor", type: "text" },
        { key: "status", label: "Status", type: "text" },
        { key: "userAgent", label: "Navegador", type: "text" },
        { key: "lastActivityAt", label: "Última atividade", type: "date" },
        { key: "expiresAt", label: "Expira em", type: "date" },
        { key: "loggedOutAt", label: "Logout em", type: "date" },
      ],
      file_access_logs: [
        { key: "createdAt", label: "Data", type: "date" },
        { key: "user", label: "Usuário", type: "user" },
        { key: "fileType", label: "Tipo de arquivo", type: "text" },
        { key: "requisicaoId", label: "Requisição (ID)", type: "text" },
        { key: "ipAddress", label: "IP", type: "text" },
      ],
    };

    const currentLogColumns = computed(
      () => logColumnDefs[logSource.value] || [],
    );

    const logCellValue = (row, def) => {
      const v = row[def.key];
      if (v === null || v === undefined) return "";
      if (def.type === "date") {
        const d = new Date(v);
        return isNaN(d.getTime())
          ? String(v)
          : d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
      }
      if (def.type === "user") {
        return v && v.nome ? `${v.nome} (${v.role || ""})` : "";
      }
      if (def.type === "json") {
        try {
          return typeof v === "string" ? v : JSON.stringify(v);
        } catch (e) {
          return "";
        }
      }
      return String(v);
    };

    const fetchLogs = async () => {
      if (!canViewLogs.value) return;
      logsLoading.value = true;
      try {
        const f = logFilters.value || {};
        const params = {
          source: logSource.value,
          page: logsPage.value,
          limit: logsLimit.value,
        };
        if (f.userId) params.userId = f.userId;
        if (f.action) params.action = f.action;
        if (f.entity) params.entity = f.entity;
        if (f.status) params.status = f.status;
        if (Array.isArray(f.date) && f.date.length === 2) {
          params.dateFrom = f.date[0];
          params.dateTo = f.date[1];
        }
        const { data } = await apiClient.get("/audit/logs", { params });
        logsData.value = data.data || [];
        logsTotal.value = data.total || 0;
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      } finally {
        logsLoading.value = false;
      }
    };

    const fetchLogUsers = async () => {
      try {
        const { data } = await apiClient.get("/audit/logs/users");
        logUsers.value = data || [];
      } catch (error) {
        logUsers.value = [];
      }
    };

    const applyLogFilters = () => {
      logsPage.value = 1;
      fetchLogs();
    };

    const clearLogFilters = () => {
      logFilters.value = {};
      logsPage.value = 1;
      fetchLogs();
    };

    const fetchLogSources = async () => {
      try {
        const { data } = await apiClient.get("/audit/logs/sources");
        logSources.value = data || [];
      } catch (error) {
        // silencioso
      }
    };

    const onLogSourceChange = () => {
      logFilters.value = {};
      logsPage.value = 1;
      fetchLogs();
    };

    const onLogsPageChange = (p) => {
      logsPage.value = p;
      fetchLogs();
    };

    // Detalhe humanizado de um registro de log (reativo conforme a fonte).
    const logDetailVisible = ref(false);
    const logDetailRow = ref(null);
    const openLogDetail = (row) => {
      logDetailRow.value = row;
      logDetailVisible.value = true;
    };
    const logDetailJson = (v) => {
      if (v === null || v === undefined || v === "") return "—";
      try {
        return typeof v === "string" ? v : JSON.stringify(v, null, 2);
      } catch (e) {
        return String(v);
      }
    };

    const checkLogAccess = async () => {
      try {
        const { data } = await apiClient.get("/audit/logs/access");
        canViewLogs.value = !!data.allowed;
        if (canViewLogs.value) {
          await fetchLogSources();
          await fetchLogUsers();
          await fetchLogs();
        }
      } catch (error) {
        canViewLogs.value = false;
      }
    };

    // Bug: aba de Registro de Livro visível para a prefeitura (somente leitura).
    // Registro de Livro: tabela paginada + filtros + detalhe.
    const registroLivroTableRef = ref(null);
    const refreshRegistrosLivro = () => {
      if (registroLivroTableRef.value) registroLivroTableRef.value.refresh();
    };
    const livroFilterFields = [
      { key: "numeroOrdem", label: "Nº Ordem" },
      { key: "nomeFalecido", label: "Falecido" },
      { key: "data", label: "Data", type: "date" },
      { key: "filiacao", label: "Filiação" },
      { key: "sexo", label: "Sexo" },
      { key: "estadoCivil", label: "Estado Civil" },
      { key: "idade", label: "Idade" },
      { key: "profissao", label: "Profissão" },
      { key: "naturalidade", label: "Naturalidade" },
      { key: "residenciaResponsavel", label: "Residência do Responsável" },
      { key: "telefoneContato", label: "Telefone" },
      { key: "referenciaExterna", label: "Referência Externa" },
      { key: "causaMorte", label: "Causa da Morte" },
      { key: "medicoAtestado", label: "Médico (atestado)" },
      { key: "tipoSepultura", label: "Tipo de Sepultura" },
      { key: "numeroSepultura", label: "Nº Sepultura" },
      { key: "quadra", label: "Quadra" },
      { key: "dataExumacao", label: "Data da Exumação", type: "date" },
      { key: "nomeResponsavel", label: "Nome do Responsável" },
      { key: "cpfResponsavel", label: "CPF do Responsável" },
      { key: "observacoes", label: "Observações" },
    ];
    const livroDetailFields = [
      ...livroFilterFields,
      { key: "cemiterio", label: "Cemitério", type: "cemiterio" },
      { key: "createdBy", label: "Preenchido por", type: "user" },
      { key: "createdAt", label: "Registrado em", type: "datetime" },
    ];
    const registroDetailVisible = ref(false);
    const registroDetailRow = ref(null);
    const openRegistroDetail = (row) => {
      registroDetailRow.value = row;
      registroDetailVisible.value = true;
    };
    const registroCellValue = (row, f) => {
      if (f.type === "cemiterio") return row.cemiterio ? row.cemiterio.nome : "";
      if (f.type === "user") return row.createdBy ? `${row.createdBy.nome} (${row.createdBy.role})` : "";
      const v = row[f.key];
      if (v === null || v === undefined) return "";
      if (f.type === "datetime") {
        const d = new Date(v);
        return isNaN(d.getTime()) ? String(v) : d.toLocaleString("pt-BR");
      }
      return String(v);
    };

    const confirmarRegistroLivro = async (row) => {
      try {
        await apiClient.patch(`/registros-livro/${row.id}/confirm`);
        ElMessage.success("Registro confirmado com sucesso!");
        refreshRegistrosLivro();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || "Erro ao confirmar registro");
      }
    };

    const userRules = {
      nome: [{ required: true, message: "Nome é obrigatório", trigger: "blur" }],
      email: [
        { required: true, message: "E-mail é obrigatório", trigger: "blur" },
        { type: "email", message: "Formato inválido", trigger: "blur" },
      ],
      password: [
        { required: true, message: "Senha é obrigatória", trigger: "blur" },
        { min: 6, message: "Min. 6 caracteres", trigger: "blur" },
      ],
      cpf: [
        { required: true, message: "CPF é obrigatório", trigger: "blur" },
      ],
      celular: [{ required: true, message: "Celular é obrigatório", trigger: "blur" }],
      telefone: [{ required: true, message: "Telefone é obrigatório", trigger: "blur" }],
      role: [{ required: true, message: "Cargo é obrigatório", trigger: "change" }],
    };

    const onSubmitUserForm = async () => {
      if (!userFormRef.value) return;
      userFormRef.value.validate(async (valid) => {
        if (!valid) return;
        try {
          const payload = {
            ...newUser.value,
            cpf: newUser.value.cpf.replace(/\D/g, ""),
          };
          await apiClient.post("/users/admin-create", payload);
          ElMessage.success("Usuário criado com sucesso!");
          Object.assign(newUser.value, {
            nome: "",
            email: "",
            password: "",
            cpf: "",
            celular: "",
            telefone: "",
            role: "",
            cemiterioId: "",
            funerariaId: "",
          });
          if (usersTableRef.value) {
            usersTableRef.value.refresh();
          }
        } catch (error) {
          ElMessage.error(getErrorMessage(error));
        }
      });
    };

    const users = ref([]);
    const loadingUsers = ref(false);
    const fetchUsers = async () => {
      loadingUsers.value = true;
      try {
        const { data } = await apiClient.get("/users");
        users.value = data;
      } catch (error) {
        console.error("Erro ao buscar usuários", error);
      } finally {
        loadingUsers.value = false;
      }
    };
    const deleteUser = async (id) => {
      try {
        await apiClient.delete(`/users/${id}`);
        ElMessage.success("Usuário excluído com sucesso!");
        if (usersTableRef.value) {
          usersTableRef.value.refresh();
        }
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    const usersFilter = ref("");
    const filteredUsers = computed(() => {
      if (!usersFilter.value) return users.value;
      return users.value.filter(row => JSON.stringify(row).toLowerCase().includes(usersFilter.value.toLowerCase()));
    });

    // ---------------- CEMITERIOS ----------------
    const cemiterios = ref([]);
    const loadingCemiterios = ref(false);
    const newCemiterio = ref({
      nome: "",
      endereco: "",
      telefone: "",
      email: "",
    });
    const cemiterioFormRef = ref(null);
    const editCemiterioDialogVisible = ref(false);
    const editCemiterio = ref({
      id: "",
      nome: "",
      endereco: "",
      telefone: "",
      email: "",
      ativo: true,
    });

    const downloadUrl = (requisicaoId, index) => {
      // index 0 = RG, 1 = DO, 2 = Comprovante de DO. O tipo 'boleto' é mantido
      // na URL por compatibilidade com o backend.
      const fileType = index === 0 ? 'rg' : index === 1 ? 'do' : 'boleto';
      return `/requisicoes/download/${requisicaoId}/${fileType}`;
    };
    
    const downloadExtraUrl = (requisicaoId, index) => {
      return `/requisicoes/download/${requisicaoId}/extra-${index}`;
    };

    const extFromContentType = (ct) => {
      const map = {
        'application/pdf': 'pdf',
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/bmp': 'bmp',
        'image/webp': 'webp',
        'image/tiff': 'tiff',
        'image/svg+xml': 'svg',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'application/vnd.ms-excel': 'xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'application/vnd.oasis.opendocument.text': 'odt',
        'text/plain': 'txt',
        'text/csv': 'csv',
      };
      const base = (ct || '').split(';')[0].trim().toLowerCase();
      return map[base] || 'bin';
    };
    const filenameFromResponse = (response, fallbackBase) => {
      const cd = response.headers['content-disposition'] || '';
      const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
      if (m && m[1]) {
        try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
      }
      return `${fallbackBase}.${extFromContentType(response.headers['content-type'])}`;
    };

    const handleDownloadFile = async (id, index) => {
      try {
        const response = await apiClient.get(downloadUrl(id, index), { responseType: 'blob' });
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const baseName = index === 0 ? 'RG' : index === 1 ? 'DO' : 'COMPROVANTE_DO';
        link.setAttribute('download', filenameFromResponse(response, baseName));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        ElMessage.error('Erro ao baixar o arquivo');
      }
    };

    const handleDownloadExtraFile = async (id, index) => {
      try {
        const response = await apiClient.get(downloadExtraUrl(id, index), { responseType: 'blob' });
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filenameFromResponse(response, `EXTRA_${index}`));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        ElMessage.error('Erro ao baixar o arquivo extra');
      }
    };

    // Fetch cemiterios (and assign to the filter list)
    const fetchCemiterios = async () => {
      loadingCemiterios.value = true;
      try {
        const { data } = await apiClient.get("/cemiterios");
        cemiterios.value = data;
        cemiteriosList.value = data;
      } catch (error) {
        console.error("Error fetching cemiterios", error);
      } finally {
        loadingCemiterios.value = false;
      }
    };

    const createCemiterio = async () => {
      if (!newCemiterio.value.nome) {
        ElMessage.warning("Nome do cemitério é obrigatório");
        return;
      }
      try {
        await apiClient.post("/cemiterios", newCemiterio.value);
        ElMessage.success("Cemitério criado com sucesso!");
        Object.assign(newCemiterio.value, { nome: "", endereco: "", telefone: "", email: "" });
        fetchCemiterios();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };
    const openEditCemiterioDialog = (cem) => {
      editCemiterio.value = { ...cem };
      editCemiterioDialogVisible.value = true;
    };
    const updateCemiterio = async () => {
      try {
        await apiClient.patch(
            `/cemiterios/${editCemiterio.value.id}`,
            editCemiterio.value,
            {
              headers: {
                Authorization: `Bearer ${userStore.token}`,
              },
            }
        );
        ElMessage.success("Cemitério atualizado com sucesso!");
        editCemiterioDialogVisible.value = false;
        fetchCemiterios();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };
    const deleteCemiterio = async (id) => {
      try {
        await apiClient.patch(
            `/cemiterios/${id}`,
            {
              headers: {
                Authorization: `Bearer ${userStore.token}`,
              },
            }
        );
        ElMessage.success("Cemitério excluído com sucesso!");
        fetchCemiterios();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    const cemiteriosFilter = ref("");
    const filteredCemiterios = computed(() => {
      if (!cemiteriosFilter.value) return cemiterios.value;
      return cemiterios.value.filter(row => JSON.stringify(row).toLowerCase().includes(cemiteriosFilter.value.toLowerCase()));
    });

    // ---------------- FUNERARIAS ----------------
    const funerarias = ref([]);
    const loadingFunerarias = ref(false);
    const newFuneraria = ref({
      nome: "",
      cnpj: "",
      endereco: "",
      telefone: "",
      email: "",
    });
    const funerariaFormRef = ref(null);
    const editFunerariaDialogVisible = ref(false);
    const editFuneraria = ref({
      id: "",
      nome: "",
      cnpj: "",
      endereco: "",
      telefone: "",
      email: "",
      ativo: true,
    });
    const fetchFunerarias = async () => {
      loadingFunerarias.value = true;
      try {
        const { data } = await apiClient.get("/funerarias");
        funerarias.value = data;
      } catch (error) {
        console.error("Error fetching funerarias", error);
      } finally {
        loadingFunerarias.value = false;
      }
    };
    const createFuneraria = async () => {
      if (!newFuneraria.value.nome) {
        ElMessage.warning("Nome da funeraria é obrigatório");
        return;
      }
      try {
        await apiClient.post("/funerarias", newFuneraria.value);
        ElMessage.success("Funeraria criada com sucesso!");
        Object.assign(newFuneraria.value, { nome: "", cnpj: "", endereco: "", telefone: "", email: "" });
        fetchFunerarias();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };
    const openEditFunerariaDialog = (fun) => {
      editFuneraria.value = { ...fun };
      editFunerariaDialogVisible.value = true;
    };
    const updateFuneraria = async () => {
      try {
        await apiClient.patch(`/funerarias/${editFuneraria.value.id}`, editFuneraria.value);
        ElMessage.success("Funeraria atualizada com sucesso!");
        editFunerariaDialogVisible.value = false;
        fetchFunerarias();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };
    const deleteFuneraria = async (id) => {
      try {
        await apiClient.delete(`/funerarias/${id}`);
        ElMessage.success("Funeraria excluída com sucesso!");
        fetchFunerarias();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };
    const toggleFunerariaStatus = async (funeraria) => {
      try {
        const newStatus = !funeraria.ativo;
        await apiClient.patch(`/funerarias/${funeraria.id}`, { ativo: newStatus });
        ElMessage.success(`Funeraria ${newStatus ? 'desbloqueada' : 'bloqueada'} com sucesso!`);
        fetchFunerarias();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    const funerariasFilter = ref("");
    const filteredFunerarias = computed(() => {
      if (!funerariasFilter.value) return funerarias.value;
      return funerarias.value.filter(row => JSON.stringify(row).toLowerCase().includes(funerariasFilter.value.toLowerCase()));
    });

    // ---------------- ADMIN VAGAS ----------------
    const adminVagas = ref([]);
    const loadingAdminVagas = ref(false);
    const fetchAdminVagas = async () => {
      loadingAdminVagas.value = true;
      try {
        const { data } = await apiClient.get("/vagas");
        adminVagas.value = data;
      } catch (error) {
        console.error("Erro ao buscar vagas", error);
      } finally {
        loadingAdminVagas.value = false;
      }
    };
    const editVagaDialogVisible = ref(false);
    const editVagaData = ref({});
    // Store the initial data for diffing
    const initialVagaData = ref(null);

    // Utility: Deep clone
    function deepClone(obj) {
      return JSON.parse(JSON.stringify(obj));
    }

    // Utility: Build diff payload
    function buildVagaDiffPayload(initial, current) {
      const payload = {};
      for (const key in current) {
        if (key === 'cemiterio') {
          // If cemiterio changed, send only the id
          const initialId = initial?.cemiterio?.id || initial?.cemiterio;
          const currentId = current?.cemiterio?.id || current?.cemiterio;
          if (initialId !== currentId) {
            payload.cemiterio = currentId;
          }
        } else if (JSON.stringify(current[key]) !== JSON.stringify(initial?.[key])) {
          payload[key] = current[key];
        }
      }
      return payload;
    }

    // Allowlist de edição (requisição/vaga): consultada no backend.
    const canEditPrivileged = ref(false);
    const fetchEditAccess = async () => {
      try {
        const { data } = await apiClient.get("/requisicoes/edit-access");
        canEditPrivileged.value = !!data.allowed;
      } catch (error) {
        canEditPrivileged.value = false;
      }
    };

    // Call this when opening the edit dialog for a vaga
    function editVaga(vaga) {
      if (!canEditPrivileged.value) {
        ElMessage.error("Você não tem permissão para editar vagas.");
        return;
      }
      editVagaData.value = { ...vaga };
      initialVagaData.value = deepClone(vaga);
      editVagaDialogVisible.value = true;
    }

    const updateVaga = async () => {
      try {
        const payload = buildVagaDiffPayload(initialVagaData.value, editVagaData.value);
        await apiClient.patch(`/vagas/${editVagaData.value.id}`, payload);
        ElMessage.success("Vaga atualizada com sucesso!");
        editVagaDialogVisible.value = false;
        if (vagasTableRef.value) {
          vagasTableRef.value.refresh();
        }
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };
    const deleteVaga = async (id) => {
      try {
        await apiClient.delete(`/vagas/${id}`);
        ElMessage.success("Vaga excluída com sucesso!");
        if (vagasTableRef.value) {
          vagasTableRef.value.refresh();
        }
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };
    const formatDisponivel = (row, column, cellValue) => (cellValue ? "Sim" : "Não");
    // eslint-disable-next-line no-unused-vars
    const formatVaga = (row, _column, _cellValue) => {
      if (row && row.vaga && row.vaga.quadra && row.vaga.numeroSepultura) {
        return `Quadra ${row.vaga.quadra} - Sep. ${row.vaga.numeroSepultura}`;
      }
      return "N/A";
    };

    const adminVagasFilter = ref("");
    const filteredAdminVagas = ref([]);
    function applyVagasFilter() {
      filteredAdminVagas.value = adminVagas.value.filter(row => {
        const cemNome = row.cemiterio && row.cemiterio.nome ? row.cemiterio.nome : '';
        const disp = row.disponivel ? 'Sim' : 'Não';
        return (
          (!vagasFilterForm.value.quadra || String(row.quadra).toLowerCase().includes(vagasFilterForm.value.quadra.toLowerCase())) &&
          (!vagasFilterForm.value.numeroSepultura || String(row.numeroSepultura).toLowerCase().includes(vagasFilterForm.value.numeroSepultura.toLowerCase())) &&
          (!vagasFilterForm.value.tipo || (row.tipo || '').toLowerCase().includes(vagasFilterForm.value.tipo.toLowerCase())) &&
          (!vagasFilterForm.value.cemiterio || cemNome.toLowerCase().includes(vagasFilterForm.value.cemiterio.toLowerCase())) &&
          (!vagasFilterForm.value.disponivel || disp === vagasFilterForm.value.disponivel) &&
          (!vagasFilterForm.value.status || (row.status || '').toLowerCase().includes(vagasFilterForm.value.status.toLowerCase()))
        );
      });
    }
    watch(adminVagas, () => { filteredAdminVagas.value = adminVagas.value.slice(); });

    // ---------------- ADMIN REQUISIÇÕES ----------------
    const adminRequisicoes = ref([]);
    const loadingAdminRequisicoes = ref(false);
    const fetchAdminRequisicoes = async () => {
      loadingAdminRequisicoes.value = true;
      try {
        const { data } = await apiClient.get("/requisicoes");
        adminRequisicoes.value = data;
      } catch (error) {
        console.error("Erro ao buscar requisições", error);
      } finally {
        loadingAdminRequisicoes.value = false;
      }
    };
    const editRequisicaoDialogVisible = ref(false);
    const editRequisicaoData = ref({});
    const editRequisicao = (req) => {
      if (!canEditPrivileged.value) {
        ElMessage.error("Você não tem permissão para editar requisições.");
        return;
      }
      // A tabela paginada traz a vaga como objeto (req.vaga.id) e não um
      // escalar req.vagaId. Sem isso, a busca de horários disponíveis na
      // edição nunca dispara (o guard exige vagaId).
      editRequisicaoData.value = {
        ...req,
        reason: '',
        vagaId: req.vagaId ?? req.vaga?.id,
      };
      // Opcional: Limpar arquivos previamente selecionados na edição
      editRgFile.value = null;
      editDoFile.value = null;
      editRgFileName.value = 'Nenhum arquivo selecionado';
      editDoFileName.value = 'Nenhum arquivo selecionado';
      // Se necessário, buscar horários disponíveis para a data atual
      if (req.dataRequisicao) {
        onEditDateChange(req.dataRequisicao);
      }
      editRequisicaoDialogVisible.value = true;
    };
    const updateRequisicao = async () => {
      if (!editRequisicaoFormRef.value) return;
      
      await editRequisicaoFormRef.value.validate(async (valid) => {
        if (!valid) return;

        // formata data para "YYYY-MM-DD"
        const dateValue = editRequisicaoData.value.dataRequisicao;
        const dataRequisicao = dateValue instanceof Date
            ? dateValue.toISOString().split('T')[0]
            : dateValue;

        // Prefeitura envia só data/horário (whitelist do backend); o usuário
        // raiz pode também alterar o nome do falecido.
        const payload = {
          dataRequisicao,
          horaRequisicao: editRequisicaoData.value.horaRequisicao,
          reason: editRequisicaoData.value.reason
        };
        if (canEditPrivileged.value) {
          payload.nomeFalecido = editRequisicaoData.value.nomeFalecido;
        }

        try {
          await apiClient.patch(
              `/requisicoes/${editRequisicaoData.value.id}`,
              payload
          );
          ElMessage.success("Requisição atualizada com sucesso!");
          editRequisicaoDialogVisible.value = false;
          if (requisicoesTableRef.value) {
            requisicoesTableRef.value.refresh();
          }
        } catch (error) {
          ElMessage.error(getErrorMessage(error));
        }
      });
    };
    function formatHorarioDesejado(row) {
      if (!row.dataRequisicao || !row.horaRequisicao) return 'N/A';
      const dateObj = new Date(row.dataRequisicao + "T00:00:00");
      const formattedDate = dateObj.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric' });
      return `${formattedDate} ${row.horaRequisicao}`;
    }
    const deleteRequisicao = async (id, reason) => {
      try {
        await apiClient.delete(`/requisicoes/${id}`, {
          data: { reason }
        });
        ElMessage.success("Requisição excluída com sucesso!");
        fetchAdminRequisicoes();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    const openDeleteRequisicaoDialog = (row) => {
      deleteRequisicaoData.value = {
        id: row.id,
        reason: ''
      };
      deleteRequisicaoDialogVisible.value = true;
    };

    const confirmDeleteRequisicao = async () => {
      if (!deleteRequisicaoData.value.reason || deleteRequisicaoData.value.reason.trim() === '') {
        ElMessage.error("Por favor, informe o motivo da exclusão.");
        return;
      }

      deletingRequisicao.value = true;
      try {
        await deleteRequisicao(deleteRequisicaoData.value.id, deleteRequisicaoData.value.reason);
        deleteRequisicaoDialogVisible.value = false;
        deleteRequisicaoData.value = { id: null, reason: '' };
      } catch (error) {
        // Error is already handled in deleteRequisicao function
      } finally {
        deletingRequisicao.value = false;
      }
    };
    const toggleRequisicaoStatus = async (requisicao) => {
      try {
        const newStatus = requisicao.status === "PERMITIDO" ? "PENDENTE" : "PERMITIDO";
        await apiClient.patch(`/requisicoes/${requisicao.id}`, { status: newStatus });
        ElMessage.success(`Requisição ${newStatus === "PERMITIDO" ? "permitida" : "retirada"} com sucesso!`);
        if (requisicoesTableRef.value) {
          requisicoesTableRef.value.refresh();
        }
        fetchPendenciasCount();
      } catch (error) {
        ElMessage.error(getErrorMessage(error));
      }
    };

    const adminRequisicoesFilter = ref("");
    const filteredAdminRequisicoes = ref([]);
    function applyRequisicoesFilter() {
      filteredAdminRequisicoes.value = adminRequisicoes.value.filter(row => {
        const cemNome = row.vaga && row.vaga.cemiterio && row.vaga.cemiterio.nome ? row.vaga.cemiterio.nome : '';
        const funNome = row.funeraria && row.funeraria.nome ? row.funeraria.nome : '';
        return (
          (!requisicoesFilterForm.value.nomeFalecido || (row.nomeFalecido || '').toLowerCase().includes(requisicoesFilterForm.value.nomeFalecido.toLowerCase())) &&
          (!requisicoesFilterForm.value.cpfFalecido || (row.cpfFalecido || '').toLowerCase().includes(requisicoesFilterForm.value.cpfFalecido.toLowerCase())) &&
          (!requisicoesFilterForm.value.dataFalecimento || (row.dataFalecimento || '').toLowerCase().includes(requisicoesFilterForm.value.dataFalecimento.toLowerCase())) &&
          (!requisicoesFilterForm.value.cemiterio || cemNome.toLowerCase().includes(requisicoesFilterForm.value.cemiterio.toLowerCase())) &&
          (!requisicoesFilterForm.value.funeraria || funNome.toLowerCase().includes(requisicoesFilterForm.value.funeraria.toLowerCase())) &&
          (!requisicoesFilterForm.value.status || (row.status || '').toLowerCase().includes(requisicoesFilterForm.value.status.toLowerCase()))
        );
      });
    }
    watch(adminRequisicoes, () => { filteredAdminRequisicoes.value = adminRequisicoes.value.slice(); });

    // fetchRequisicoesAndamento removed - no longer needed with pagination

    watch(selectedCemiterio, () => {
      fetchMapaSepultamento();
      fetchApprovedRequisicoes();
    });
    watch([selectedDate, startDate, endDate], () => {
      fetchMapaSepultamento();
      fetchApprovedRequisicoes();
    });

    onMounted(async () => {
      if (activeTab.value === 'graficos') {
        await fetchCharts();
        await nextTick();
        await resizeAllCharts();
      }
    });

    onMounted(() => {
      const today = new Date().toISOString().split("T")[0];
      selectedDate.value = today;
      if (new Date().getHours() >= 9) {
        fetchMapaSepultamento();
        fetchApprovedRequisicoes();
      }
      // fetchUsers(); // PaginatedTable gerencia automaticamente
      fetchCemiterios();
      fetchFunerarias();
      // fetchAdminVagas(); // PaginatedTable gerencia automaticamente
      // fetchAdminRequisicoes(); // PaginatedTable gerencia automaticamente
      fetchApprovedRequisicoes();
      fetchPendenciasCount();
      checkLogAccess();
      fetchEditAccess();
      // Auto-refresh disabled for serverless compatibility
      // intervalId = setInterval(() => {
      //   if (activeTab.value === 'users') fetchUsers();
      //   if (activeTab.value === 'cemiterios') fetchCemiterios();
      //   if (activeTab.value === 'funerarias') fetchFunerarias();
      //   if (activeTab.value === 'vagas') fetchAdminVagas();
      //   if (activeTab.value === 'requisicoes') {
      //     fetchRequisicoesAndamento();
      //     fetchAdminRequisicoes();
      //   }
      //   if (activeTab.value === 'mapa' && new Date().getHours() >= 9) {
      //     fetchMapaSepultamento();
      //     fetchApprovedRequisicoes();
      //   }
      //   if (activeTab.value === 'graficos') {
      //     fetchCharts();
      //   }
      // }, 300000);
    });

    const approvedRequisicoesFilter = ref("");
    const filteredApprovedRequisicoes = computed(() => {
      if (!approvedRequisicoesFilter.value) return approvedRequisicoes.value;
      return approvedRequisicoes.value.filter(row => JSON.stringify(row).toLowerCase().includes(approvedRequisicoesFilter.value.toLowerCase()));
    });

    // ---------------- ADMIN VAGAS ----------------
    const vagasFilterForm = ref({
      quadra: '',
      numeroSepultura: '',
      tipo: '',
      cemiterio: '',
      disponivel: '',
      status: '',
    });

    // ---------------- ADMIN REQUISIÇÕES ----------------
    const requisicoesFilterForm = ref({
      nomeFalecido: '',
      cpfFalecido: '',
      dataFalecimento: '',
      cemiterio: '',
      funeraria: '',
      status: '',
    });

    const greetingMessage = computed(() => {
      const now = new Date();
      const hour = now.getHours();
      let greeting = "Bom dia";
      if (hour >= 18 || hour < 5) {
        greeting = "Boa noite";
      } else if (hour >= 12) {
        greeting = "Boa tarde";
      }
      const nome = userStore.user && userStore.user.nome ? userStore.user.nome : "Usuário";
      return `${greeting}, ${nome} (Prefeitura)`;
    });

    return {
      activeTab,
      userStore,
      newUser,
      userFormRef,
      usersTableRef,
      vagasTableRef,
      requisicoesTableRef,
      pendenciasCount,
      fetchPendenciasCount,
      canViewLogs,
      logSources,
      logSource,
      logsData,
      logsTotal,
      logsPage,
      logsLimit,
      logsLoading,
      currentLogColumns,
      logCellValue,
      fetchLogs,
      onLogSourceChange,
      onLogsPageChange,
      logFilters,
      logUsers,
      currentLogFilters,
      applyLogFilters,
      clearLogFilters,
      logDetailVisible,
      logDetailRow,
      openLogDetail,
      logDetailJson,
      registroLivroTableRef,
      refreshRegistrosLivro,
      livroFilterFields,
      livroDetailFields,
      registroDetailVisible,
      registroDetailRow,
      openRegistroDetail,
      registroCellValue,
      confirmarRegistroLivro,
      userRules,
      onSubmitUserForm,
      users,
      loadingUsers,
      fetchUsers,
      deleteUser,
      cemiterios,
      loadingCemiterios,
      newCemiterio,
      cemiterioFormRef,
      editCemiterioDialogVisible,
      editCemiterio,
      fetchCemiterios,
      createCemiterio,
      openEditCemiterioDialog,
      updateCemiterio,
      deleteCemiterio,
      funerarias,
      loadingFunerarias,
      newFuneraria,
      funerariaFormRef,
      editFunerariaDialogVisible,
      editFuneraria,
      fetchFunerarias,
      createFuneraria,
      openEditFunerariaDialog,
      updateFuneraria,
      deleteFuneraria,
      toggleFunerariaStatus,
      adminVagas,
      loadingAdminVagas,
      editVaga,
      editVagaDialogVisible,
      editVagaData,
      updateVaga,
      deleteVaga,
      formatDisponivel,
      formatVaga,
      adminRequisicoes,
      loadingAdminRequisicoes,
      editRequisicao,
      editRequisicaoDialogVisible,
      editRequisicaoData,
      updateRequisicao,
      onEditDateChange,
      availableTimeSlotsEdit,
      loadingTimeSlotsEdit,
      canEditPrivileged,
      deleteRequisicao,
      openDeleteRequisicaoDialog,
      confirmDeleteRequisicao,
      deleteRequisicaoDialogVisible,
      deleteRequisicaoData,
      deleteRequisicaoFormRef,
      deletingRequisicao,
      cancelRequisicao,
      toggleRequisicaoStatus,
      formatDateCell,
      selectedCemiterio,
      cemiteriosList,
      mapaSepultamento,
      downloadPDFMap,
      formatHorarioDesejado,
      formatDateToRio,
      formatDateToBrazil,
      // New reactive properties for the Mapa download UI
      mapDownloadMode,
      selectedDate,
      startDate,
      endDate,
      // Expose disabledDate functions for Intervalo mode
      disabledStartDate,
      disabledEndDate,
      approvedRequisicoes,
      fetchApprovedRequisicoes,
      confirmSepultamento,
      parseCaminhoArquivos,
      downloadUrl,
      handleDownloadFile,
      parseArquivosExtras,
      handleDownloadExtraFile,
      editRequisicaoFormRef,
      onEditRGFileChange,
      onEditDOFileChange,
      onEditRGFileRemove,
      onEditDOFileRemove,
      disabledRequisicaoDateEdit,
      chartStartDate,
      chartEndDate,
      fetchCharts,
      requestsPerDayOptions,
      statusDistributionOptions,
      requestsPerFunerariaOptions,
      requestsPerCemiterioOptions,
      vacanciesByTypeOptions,
      vacancyOccupationOptions,
      requestsMatrixOptions,
      availableVagasOptions,
      requestsPerDayChart,
      statusDistributionChart,
      requestsPerFunerariaChart,
      requestsPerCemiterioChart,
      vacanciesByTypeChart,
      vacancyOccupationChart,
      requestsMatrixChart,
      availableVagasChart,
      usersFilter,
      cemiteriosFilter,
      funerariasFilter,
      adminVagasFilter,
      adminRequisicoesFilter,
      approvedRequisicoesFilter,
      filteredUsers,
      filteredCemiterios,
      filteredFunerarias,
      filteredAdminVagas,
      filteredAdminRequisicoes,
      filteredApprovedRequisicoes,
      vagasFilterForm,
      applyVagasFilter,
      requisicoesFilterForm,
      applyRequisicoesFilter,
      greetingMessage,
      // Status de Funerárias
      funerariasStatus,
      loadingFunerariasStatus,
      funerariasStatusFilter,
      removingBloqueio,
      filteredFunerariasStatus,
      fetchFunerariasStatus,
      isBlocked,
      formatDateTime,
      formatMinutesRemaining,
      confirmRemoveBloqueio,
      removeBloqueio,
    };
  },
};
</script>

<style scoped>
.admin-prefeitura-dashboard {
  padding: 12px 24px;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.tab-content {
  margin-top: 20px;
}
.flex-container {
  display: flex;
  gap: 20px;
}
.flex-item {
  flex: 1;
}
.mt-20 {
  margin-top: 20px;
}
.mapa-container {
  padding: 20px;
}
.filtro {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.admin-prefeitura-dashboard ::v-deep .el-tabs__content {
  overflow: visible !important;
}

.charts-container {
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
}
.charts-container > div {
  width: 100% !important;
}

/* Estilos para aba de Status de Funerárias */
.empty-state {
  text-align: center;
  padding: 40px;
  color: #909399;
}

.text-muted {
  color: #909399;
  font-style: italic;
}

.text-warning {
  color: #e6a23c;
  font-weight: 500;
}

.text-success {
  color: #67c23a;
  font-weight: 500;
}

.reservation-details {
  max-width: 300px;
}

.reservation-item {
  margin-bottom: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #eee;
}

.reservation-item:last-child {
  border-bottom: none;
}
</style>